import { state } from './state.js';
import { expandViewer, refreshViewerEmptyState, requestWorkflowScroll } from './workflow_selection.js';
import * as API from './api.js';
import { showToast } from './ui/toast.js';
import { openConfirmModal, openPlanComparisonModal } from './modal_manager.js';
import { acquirePageScrollLock, getPageScrollLockCount } from './ui/scroll_lock.js';
import { promptSignInForDownload } from './auth.js';
import {
    normalizeSettingUnits,
    toUiColorShare,
    toUiNoiseFactor,
    toUiReusePenalty,
    toUiTileHeight,
} from './config/setting_units.js';
import { JOB_MONITORING } from './config/jobMonitoring.js';
import {
    clearBillingReturnContext,
    getBillingReturnContext,
    normalizeBillingReturnContext,
    saveBillingReturnContext,
} from './result_viewer/billing_return_context.js';
import {
    refreshActiveSocialVideoUi,
    resetSocialVideoUi,
} from './result_viewer/social_video.js';

let resizeHandlerAttached = false;
let nerdStatsBound = false;
let workflowRefitBound = false;
const RESULT_LOADER_DEFAULT_HTML = '<div class="spinner"></div><span>Loading...</span>';

function fitCompareToViewport(container, resImg, origImg) {
    if (!container || !resImg || !origImg) return;
    if (!resImg.naturalWidth || !resImg.naturalHeight) return;

    const maxWidth = getCompareMaxWidth(container);
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
    const padding = 32;
    const maxHeight = Math.max(200, window.innerHeight - headerHeight - padding);

    const scaleToWidth = maxWidth / resImg.naturalWidth;
    const heightAtWidth = resImg.naturalHeight * scaleToWidth;
    let targetWidth = maxWidth;
    let targetHeight = heightAtWidth;

    if (heightAtWidth > maxHeight) {
        const scaleToHeight = (maxHeight * 0.8) / resImg.naturalHeight;
        targetWidth = Math.floor(resImg.naturalWidth * scaleToHeight);
        targetHeight = Math.floor(resImg.naturalHeight * scaleToHeight);
    } else {
        targetHeight = Math.floor(heightAtWidth);
    }

    container.style.width = `${targetWidth}px`;
    container.style.height = `${targetHeight}px`;
    container.style.minHeight = '0px';
    container.style.maxWidth = '100%';
    container.style.marginLeft = 'auto';
    container.style.marginRight = 'auto';

    resImg.style.width = '100%';
    resImg.style.height = '100%';
    const actualWidth = container.clientWidth || targetWidth;
    const actualHeight = container.clientHeight || targetHeight;
    origImg.style.width = `${actualWidth}px`;
    origImg.style.height = `${actualHeight}px`;
}

function runDeferredRefit(container, resImg, origImg) {
    const doFit = () => {
        fitCompareToViewport(container, resImg, origImg);
        updateCompareDebug();
    };
    doFit();
    [90, 240, 520, 1100, 1800].forEach((delay) => {
        window.setTimeout(doFit, delay);
    });
}

function bindWorkflowRefit() {
    if (workflowRefitBound) return;
    workflowRefitBound = true;
    document.addEventListener('workflow:sequence', (event) => {
        const detail = (event && event.detail) || {};
        const phase = String(detail.phase || '');
        if (phase !== 'collapse_end' && phase !== 'scroll_end' && phase !== 'stage_transition_end') return;
        refitViewer();
    });
}

function fitCompareToSize(container, resImg, origImg, width, height) {
    if (!container || !resImg || !origImg) return;
    const w = Number(width || 0);
    const h = Number(height || 0);
    if (!w || !h) return;

    const maxWidth = getCompareMaxWidth(container);
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
    const padding = 32;
    const maxHeight = Math.max(200, window.innerHeight - headerHeight - padding);

    const scaleToWidth = maxWidth / w;
    const heightAtWidth = h * scaleToWidth;
    let targetWidth = maxWidth;
    let targetHeight = heightAtWidth;

    if (heightAtWidth > maxHeight) {
        const scaleToHeight = (maxHeight * 0.8) / h;
        targetWidth = Math.floor(w * scaleToHeight);
        targetHeight = Math.floor(h * scaleToHeight);
    } else {
        targetHeight = Math.floor(heightAtWidth);
    }

    container.style.width = `${targetWidth}px`;
    container.style.height = `${targetHeight}px`;
    container.style.minHeight = '0px';
    container.style.maxWidth = '100%';
    container.style.marginLeft = 'auto';
    container.style.marginRight = 'auto';

    resImg.style.width = '100%';
    resImg.style.height = '100%';
    const actualWidth = container.clientWidth || targetWidth;
    const actualHeight = container.clientHeight || targetHeight;
    origImg.style.width = `${actualWidth}px`;
    origImg.style.height = `${actualHeight}px`;
}

const addDownloadFlag = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    const joiner = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${joiner}download=1`;
};

const addLongEdgeParam = (rawUrl, longEdgePx) => {
    if (!rawUrl) return rawUrl;
    const edge = Number(longEdgePx || 0);
    if (!Number.isFinite(edge) || edge <= 0) return rawUrl;
    const joiner = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${joiner}long_edge=${Math.round(edge)}`;
};

const EXPORT_SIZES = [1600, 3200, 8000, 12000];
const DRAFT_DIRECT_DOWNLOAD_EDGE = 1600;
const BASE_FREE_PRICES = {
    3200: 999,
    8000: 2599,
    12000: 5599,
};
const PRINT_DPI_DEFAULT = 300;
const PAPER_SIZES_EU = [
    { name: 'A6', longIn: 5.83, shortIn: 4.13 },
    { name: 'A5', longIn: 8.27, shortIn: 5.83 },
    { name: 'A4', longIn: 11.69, shortIn: 8.27 },
    { name: 'A3', longIn: 16.54, shortIn: 11.69 },
    { name: 'A2', longIn: 23.39, shortIn: 16.54 },
    { name: 'A1', longIn: 33.11, shortIn: 23.39 },
];
const PAPER_SIZES_US = [
    { name: '4x6', longIn: 6, shortIn: 4 },
    { name: '5x7', longIn: 7, shortIn: 5 },
    { name: '8x10', longIn: 10, shortIn: 8 },
    { name: '11x14', longIn: 14, shortIn: 11 },
    { name: '12x18', longIn: 18, shortIn: 12 },
    { name: '16x20', longIn: 20, shortIn: 16 },
    { name: '20x30', longIn: 30, shortIn: 20 },
    { name: '24x36', longIn: 36, shortIn: 24 },
];
let _downloadModalBound = false;
let _releaseDownloadModalScrollLock = null;
let _activeDownloadBaseUrl = '';
let _activeDownloadConfig = null;
const _activeFinalizePolls = new Set();
const _activeFinalizeFlowStarts = new Set();
const _finalizeProgressByRun = new Map();
let _finalizeOptionsState = {
    jobId: '',
    optionsBySize: new Map(),
    entitlements: null,
    staleReconcile: null,
};
const BILLING_RECONCILE_TIMEOUT_MS = 90 * 1000;
const BILLING_RECONCILE_INTERVAL_MS = 2500;
const UPSELL_SESSION_CAP_KEY = 'mosaic_upsell_surface_caps_v1';
const UPSELL_SURFACE_DOWNLOAD_MODAL = 'download_modal';
const UPSELL_SURFACE_DOWNLOAD_LOCKED = 'download_locked_option';
const LIVE_PREVIEW_PHASES = new Set(['rendering', 'building']);
const LIVE_PREVIEW_MAX_FETCH_PER_SEC = 18;
const LIVE_PREVIEW_MIN_FETCH_MS = Math.max(55, Math.round(1000 / LIVE_PREVIEW_MAX_FETCH_PER_SEC));

const _livePreviewState = {
    jobId: '',
    latestVersion: 0,
    queue: [],
    inflight: false,
    token: 0,
    abortController: null,
    lastFetchStartedAt: 0,
};
const _sourcePreloadState = {
    identity: '',
    src: '',
    promise: null,
    ready: false,
    failed: false,
};
let _compareIntroToken = 0;
let _viewerPopoutToken = 0;

function _billingContextCurrentUserId() {
    return String(state.userId || localStorage.getItem('mosaic_user_id') || '').trim();
}

function _normalizeBillingReturnContext(raw) {
    return normalizeBillingReturnContext(raw);
}

function _saveBillingReturnContext(rawContext) {
    saveBillingReturnContext(rawContext, { currentUserId: _billingContextCurrentUserId() });
}

export function clearSavedBillingReturnContext() {
    clearBillingReturnContext();
}

export function getSavedBillingReturnContext() {
    return getBillingReturnContext({ currentUserId: _billingContextCurrentUserId() });
}

export async function openDownloadModalWhenReady(jobId = '', options = {}) {
    const safeJobId = String(jobId || _activeJobId() || '').trim();
    const timeoutMs = Math.max(500, Number(options.timeoutMs || 4000));
    const pollMs = Math.max(50, Number(options.pollMs || 100));
    const startedAt = Date.now();

    if (safeJobId) {
        await _refreshBillingStatus();
        await _refreshFinalizeOptions(safeJobId);
        _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
    }

    while (Date.now() - startedAt < timeoutMs) {
        const btn = document.getElementById('downloadBtn');
        if (btn && typeof btn.click === 'function' && typeof btn.onclick === 'function') {
            btn.click();
            return true;
        }
        await new Promise((resolve) => setTimeout(resolve, pollMs));
    }

    _openDownloadModal();
    return false;
}

function _formatUsd(cents) {
    const value = Number(cents || 0) / 100;
    return `$${value.toFixed(2).replace(/\\.00$/, '')}`;
}

function _format1(value) {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return '0.0';
    return num.toFixed(1);
}

function _sizeBucketLabel(size) {
    const edge = Number(size || 0);
    if (!Number.isFinite(edge) || edge <= 0) return 'Export';
    if (edge <= 1600) return 'Small';
    if (edge <= 3200) return 'Medium';
    if (edge <= 8000) return 'Large';
    return 'XLarge';
}

function _sizeBucketUseText(size) {
    const edge = Number(size || 0);
    if (!Number.isFinite(edge) || edge <= 0) return 'general use';
    if (edge <= 1600) return 'web/phone';
    if (edge <= 3200) return 'home prints';
    if (edge <= 8000) return 'poster prints';
    return 'large format';
}

function _sizeBucketExportLabel(size) {
    const bucket = _sizeBucketLabel(size);
    if (bucket === 'Export') return 'Export';
    return `${bucket} export`;
}

function _sizeBucketExportPhrase(size) {
    const bucket = _sizeBucketLabel(size);
    if (bucket === 'Export') return 'export';
    return `${bucket.toLowerCase()} export`;
}

function _normalizeLongShort(a, b) {
    const first = Math.max(0, Number(a || 0));
    const second = Math.max(0, Number(b || 0));
    if (!first || !second) return { longPx: 0, shortPx: 0 };
    return first >= second
        ? { longPx: first, shortPx: second }
        : { longPx: second, shortPx: first };
}

function _resolveAspectContext(config = {}, historyJob = null) {
    let width = Number(historyJob?.output_width || 0);
    let height = Number(historyJob?.output_height || 0);

    if (!width || !height) {
        width = Number((config || {}).w_final || 0);
        height = Number((config || {}).h_final || 0);
    }
    if (!width || !height) {
        const resImg = document.getElementById('resImg');
        width = Number((resImg && resImg.naturalWidth) || 0);
        height = Number((resImg && resImg.naturalHeight) || 0);
    }

    const normalized = _normalizeLongShort(width, height);
    const longPx = Number(normalized.longPx || 0);
    const shortPx = Number(normalized.shortPx || 0);
    const ratio = (longPx > 0 && shortPx > 0) ? (shortPx / longPx) : (2 / 3);
    return {
        longPx,
        shortPx,
        ratio: Math.max(0.05, Math.min(1, ratio)),
    };
}

function _longEdgeSummary(sizePx, dpi) {
    const size = Math.max(1, Number(sizePx || 0));
    const safeDpi = Math.max(1, Number(dpi || PRINT_DPI_DEFAULT));
    const longIn = size / safeDpi;
    const longCm = longIn * 2.54;
    return `${Math.round(size)}px • ${_format1(longIn)}" / ${_format1(longCm)} cm long side`;
}

function _computePrintSize(sizePx, dpi, aspectRatio) {
    const size = Math.max(1, Number(sizePx || 0));
    const safeDpi = Math.max(1, Number(dpi || PRINT_DPI_DEFAULT));
    const ratio = Math.max(0.05, Math.min(1, Number(aspectRatio || 2 / 3)));
    const longPx = size;
    const shortPx = Math.max(1, size * ratio);
    const longIn = longPx / safeDpi;
    const shortIn = shortPx / safeDpi;
    return {
        longIn,
        shortIn,
        longCm: longIn * 2.54,
        shortCm: shortIn * 2.54,
    };
}

function _bestPaperMatch(longIn, shortIn, paperList = []) {
    if (!Number.isFinite(longIn) || !Number.isFinite(shortIn) || !paperList.length) return null;
    const targetRatio = Math.max(longIn, shortIn) / Math.max(0.0001, Math.min(longIn, shortIn));
    let best = null;
    paperList.forEach((paper) => {
        const pLong = Math.max(Number(paper.longIn || 0), Number(paper.shortIn || 0));
        const pShort = Math.min(Number(paper.longIn || 0), Number(paper.shortIn || 0));
        if (pLong <= 0 || pShort <= 0) return;
        const pRatio = pLong / pShort;
        const ratioErr = Math.abs(Math.log(targetRatio) - Math.log(pRatio));
        const longErr = Math.abs(Math.log(Math.max(0.01, longIn) / pLong));
        const shortErr = Math.abs(Math.log(Math.max(0.01, shortIn) / pShort));
        const score = (ratioErr * 2.3) + longErr + shortErr;
        if (!best || score < best.score) {
            best = { ...paper, score };
        }
    });
    return best;
}

function _paperSummary(printSize) {
    const longIn = Math.max(printSize.longIn || 0, printSize.shortIn || 0);
    const shortIn = Math.min(printSize.longIn || 0, printSize.shortIn || 0);
    const eu = _bestPaperMatch(longIn, shortIn, PAPER_SIZES_EU);
    const us = _bestPaperMatch(longIn, shortIn, PAPER_SIZES_US);
    const euLabel = eu ? `${eu.name}-ish` : '';
    const usLabel = us ? `${us.name}-ish` : '';
    if (euLabel && usLabel) return `${euLabel} / ${usLabel}`;
    return euLabel || usLabel || 'Custom size';
}

async function _refreshBillingStatus() {
    try {
        const payload = await API.getBillingStatus();
        state.billingStatus = payload || null;
        if (payload && payload.entitlements) {
            if (!state.plan) state.plan = {};
            state.plan.entitlements = payload.entitlements;
            if (payload.entitlements.plan_name) state.plan.name = payload.entitlements.plan_name;
        }
        return payload || null;
    } catch (e) {
        return null;
    }
}

function _getEntitlements() {
    return (
        (state.billingStatus && state.billingStatus.entitlements) ||
        ((state.plan || {}).entitlements || {}) ||
        {}
    );
}

function _currentTier(entitlements = null) {
    const src = entitlements || _getEntitlements() || {};
    return String(src.tier || 'free').trim().toLowerCase();
}

function _includedExportBuckets(entitlements = null) {
    const src = entitlements || _getEntitlements() || {};
    const result = {};
    const included = src.included_exports || {};
    const bySize = included.by_size || {};
    if (bySize && typeof bySize === 'object') {
        Object.entries(bySize).forEach(([key, value]) => {
            const row = (value && typeof value === 'object') ? value : {};
            const size = Number(row.size_px || key || 0);
            if (!Number.isFinite(size) || size <= 0) return;
            const total = Number(row.total || 0);
            const used = Number(row.used || 0);
            const remaining = Number(row.remaining || Math.max(0, total - used));
            result[Math.floor(size)] = {
                size_px: Math.floor(size),
                total: Math.max(0, Math.floor(total)),
                used: Math.max(0, Math.floor(used)),
                remaining: Math.max(0, Math.floor(remaining)),
            };
        });
    }
    if (Object.keys(result).length) return result;

    const monthly = src.monthly_included_exports || {};
    const edge = Number(monthly.long_edge_px || 0);
    if (Number.isFinite(edge) && edge > 0) {
        result[Math.floor(edge)] = {
            size_px: Math.floor(edge),
            total: Math.max(0, Math.floor(Number(monthly.total || 0))),
            used: Math.max(0, Math.floor(Number(monthly.used || 0))),
            remaining: Math.max(0, Math.floor(Number(monthly.remaining || 0))),
        };
    }
    return result;
}

function _readUpsellSessionCaps() {
    try {
        const raw = sessionStorage.getItem(UPSELL_SESSION_CAP_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed;
    } catch (_e) {
        return {};
    }
}

function _writeUpsellSessionCaps(caps) {
    try {
        sessionStorage.setItem(UPSELL_SESSION_CAP_KEY, JSON.stringify(caps || {}));
    } catch (_e) {}
}

function _hasSeenUpsellSurface(surface) {
    const key = String(surface || '').trim();
    if (!key) return false;
    const caps = _readUpsellSessionCaps();
    return Boolean(caps[key]);
}

function _consumeUpsellSurface(surface, metadata = {}) {
    const key = String(surface || '').trim();
    if (!key) return false;
    if (_hasSeenUpsellSurface(key)) return false;
    const caps = _readUpsellSessionCaps();
    caps[key] = Date.now();
    _writeUpsellSessionCaps(caps);
    API.logEvent('upsell_prompt_shown', {
        surface: key,
        tier: _currentTier(),
        ...metadata,
    }).catch(() => {});
    return true;
}

function _logUpsellAction(surface, action, metadata = {}) {
    API.logEvent('upsell_prompt_action', {
        surface: String(surface || '').trim() || 'unknown',
        action: String(action || '').trim() || 'unknown',
        tier: _currentTier(),
        ...metadata,
    }).catch(() => {});
}

function _clearFinalizeOptionsState() {
    _finalizeOptionsState = {
        jobId: '',
        optionsBySize: new Map(),
        entitlements: null,
        staleReconcile: null,
    };
}

function _getFinalizeOptionForSize(size) {
    const safeSize = Number(size || 0);
    if (!Number.isFinite(safeSize) || safeSize <= 0) return null;
    if (!_finalizeOptionsState || !_finalizeOptionsState.optionsBySize) return null;
    return _finalizeOptionsState.optionsBySize.get(safeSize) || null;
}

async function _refreshFinalizeOptions(baseJobId) {
    const safeJobId = String(baseJobId || _activeJobId() || '').trim();
    if (!safeJobId) {
        _clearFinalizeOptionsState();
        return null;
    }
    try {
        const payload = await API.finalizeOptions(safeJobId);
        const rows = Array.isArray(payload?.options) ? payload.options : [];
        const nextMap = new Map();
        rows.forEach((row) => {
            const edge = Number((row || {}).size || 0);
            if (!Number.isFinite(edge) || edge <= 0) return;
            nextMap.set(edge, row || {});
        });
        _finalizeOptionsState = {
            jobId: safeJobId,
            optionsBySize: nextMap,
            entitlements: payload?.entitlements || null,
            staleReconcile: payload?.stale_reconcile || null,
        };
        return payload || null;
    } catch (_e) {
        _clearFinalizeOptionsState();
        return null;
    }
}

function _hasPaidIntentForJobSize(jobId, size) {
    const safeJobId = String(jobId || '').trim();
    const safeSize = Number(size || 0);
    if (!safeJobId || !Number.isFinite(safeSize) || safeSize <= 0) return false;
    const intents = (((state.billingStatus || {}).export_intents) || []);
    if (!Array.isArray(intents) || !intents.length) return false;
    return intents.some((intent) => {
        const intentJobId = String((intent || {}).job_id || '').trim();
        const intentSize = Number((intent || {}).long_edge_px || 0);
        const status = String((intent || {}).status || '').trim().toLowerCase();
        return intentJobId === safeJobId
            && intentSize === safeSize
            && (status === 'paid' || status === 'fulfilled');
    });
}

function _priceForSize(size, entitlements) {
    if (Boolean((entitlements || {}).admin_unlimited)) return 0;
    const catalogPrice = _catalogPriceForSize(size, entitlements);
    if (Number.isFinite(catalogPrice) && catalogPrice >= 0) return catalogPrice;

    const base = Number(BASE_FREE_PRICES[size] || 0);
    const tier = String((entitlements.tier || 'free')).toLowerCase();
    if (tier === 'free') return base;
    const discount = Number((((entitlements || {}).discount_rates || {}).export_addon || 0));
    return Math.max(0, Math.round(base * (1 - discount)));
}

function _catalogPriceForSize(size, entitlements) {
    if (Boolean((entitlements || {}).admin_unlimited)) return 0;
    const catalog = (((state.billingStatus || {}).export_catalog) || []);
    if (!Array.isArray(catalog) || !catalog.length) return null;

    const requestedEdge = Number(size || 0);
    if (!Number.isFinite(requestedEdge) || requestedEdge <= 0) return null;

    let product = null;
    for (const item of catalog) {
        const edge = Number((item || {}).long_edge_px || 0);
        if (edge >= requestedEdge) {
            product = item || null;
            break;
        }
    }
    if (!product) {
        product = catalog[catalog.length - 1] || null;
    }
    if (!product) return null;

    const tier = String(((entitlements || {}).tier || 'free')).toLowerCase();
    if (tier === 'free') {
        return Number(product.price_cents_free || 0);
    }
    if (tier === 'paid') {
        return Number(product.price_cents_paid || product.price_cents_free || 0);
    }
    return Number(product.price_cents_pro || product.price_cents_paid || product.price_cents_free || 0);
}

function _activeJobId() {
    return String(state.viewerJobId || state.selectedHistoryJobId || state.jobId || '');
}

function _authHeaders() {
    return {};
}

function _extractErrorMessage(payload, fallback = 'Download failed.') {
    if (!payload) return fallback;
    if (typeof payload === 'string') return payload;
    const detail = payload.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        if (typeof detail.message === 'string') return detail.message;
        if (typeof detail.reason === 'string') return detail.reason;
    }
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.error === 'string') return payload.error;
    return fallback;
}

function _downloadFilename(size, finalRunId = '') {
    const jobId = _activeJobId() || 'result';
    const edge = Math.max(1, Number(size || 0) || DRAFT_DIRECT_DOWNLOAD_EDGE);
    if (finalRunId) {
        return `mosaic_${jobId}_${edge}px_${finalRunId}.jpg`;
    }
    return `mosaic_${jobId}_${edge}px.jpg`;
}

async function _downloadUrlAsFile(url, filename) {
    const res = await fetch(url, {
        headers: _authHeaders(),
        credentials: 'include',
    });
    if (!res.ok) {
        let payload = null;
        try {
            payload = await res.json();
        } catch (_e) {}
        const detail = payload && typeof payload === 'object' ? payload.detail : null;
        const reason = detail && typeof detail === 'object' ? String(detail.reason || '').trim().toLowerCase() : '';
        if (res.status === 401 && reason === 'login_required') {
            promptSignInForDownload(_activeJobId());
            throw new Error('Sign in required to download your mosaic.');
        }
        throw new Error(_extractErrorMessage(payload, `Download failed (HTTP ${res.status}).`));
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function _downloadUrlDirect(url, filename = '') {
    const href = String(url || '').trim();
    if (!href) {
        throw new Error('Download URL is missing.');
    }
    const a = document.createElement('a');
    a.href = href;
    if (filename) a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
}

function _normalizeDownloadTransport(rawMode) {
    const mode = String(rawMode || '').trim().toLowerCase();
    return mode === 'direct' ? 'direct' : 'blob';
}

async function _downloadWithToasts(url, filename, toastConfig = {}) {
    const cfg = (typeof toastConfig === 'string')
        ? { pendingMessage: toastConfig }
        : (toastConfig || {});
    const pendingTitle = String(cfg.pendingTitle || 'Preparing download');
    const pendingMessage = String(cfg.pendingMessage || 'Fetching image...');
    const successTitle = String(cfg.successTitle || 'Download started');
    const successMessage = String(cfg.successMessage || 'Your file download has started.');
    const flow = String(cfg.flow || 'generic');
    const transport = _normalizeDownloadTransport(cfg.transport);
    const jobId = _activeJobId();
    const flowAction = `download_prepare_${flow}`;

    const toast = showToast(pendingTitle, pendingMessage, 0);
    API.logUiFlow('result', flowAction, 'ok', { job_id: jobId, transport }).catch(() => {});
    API.logEvent('download_prepare_started', {
        job_id: jobId,
        flow,
        transport,
    }).catch(() => {});

    const startedAt = Date.now();
    try {
        const safeUrl = String(url || '').trim();
        if (!safeUrl) {
            throw new Error('Download URL is missing.');
        }
        if (transport === 'direct') {
            _downloadUrlDirect(safeUrl, filename);
        } else {
            await _downloadUrlAsFile(safeUrl, filename);
        }
        if (toast) toast.close();
        showToast(successTitle, successMessage, 2200);
        API.logUiFlow(
            'result',
            `${flowAction}_complete`,
            'ok',
            { job_id: jobId, transport },
            Date.now() - startedAt,
        ).catch(() => {});
        API.logEvent('download_prepare_completed', {
            job_id: jobId,
            flow,
            transport,
            duration_ms: Date.now() - startedAt,
        }).catch(() => {});
    } catch (err) {
        if (toast) toast.close();
        API.logUiFlow(
            'result',
            `${flowAction}_complete`,
            'error',
            {
                job_id: jobId,
                transport,
                error: String(err?.message || err || ''),
            },
            Date.now() - startedAt,
        ).catch(() => {});
        API.logEvent('download_prepare_failed', {
            job_id: jobId,
            flow,
            transport,
            error: String(err?.message || err || ''),
            duration_ms: Date.now() - startedAt,
        }).catch(() => {});
        throw err;
    }
}

function _nextFinalizeProgressPct(finalRunId, status) {
    const key = String(finalRunId || '');
    const prev = Number(_finalizeProgressByRun.get(key) || 0);
    const stateValue = String(status || '').toLowerCase();
    let next = prev;

    if (stateValue === 'queued') {
        next = Math.min(Math.max(prev + 6, 15), 45);
    } else if (stateValue === 'pending_payment') {
        next = Math.min(Math.max(prev + 3, 20), 38);
    } else if (stateValue === 'running') {
        next = Math.min(Math.max(prev + 4, 46), 96);
    } else if (stateValue === 'complete') {
        next = 100;
    } else if (stateValue === 'failed' || stateValue === 'cancelled') {
        next = 0;
    } else {
        next = Math.min(Math.max(prev + 3, 10), 70);
    }

    if (key) _finalizeProgressByRun.set(key, next);
    return next;
}

function _clearFinalizeProgressForRun(finalRunId) {
    const key = String(finalRunId || '');
    if (!key) return;
    _finalizeProgressByRun.delete(key);
}

function _setFinalizeProgressUi(status, longEdgePx, options = {}) {
    const progressArea = document.getElementById('progressArea');
    const resultCard = document.getElementById('resultCard');
    const resultLoader = document.getElementById('resultLoader');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    const stepLabel = document.getElementById('progressStepLabel');
    const stepPct = document.getElementById('progressStepPct');
    const stepDesc = document.getElementById('progressStepDesc');
    const bar = document.getElementById('progressBar');
    const stopBtn = document.getElementById('stopBtn');
    const safeEdge = Number(longEdgePx || 0);
    const stateValue = String(status || '').toLowerCase();
    const exportLabel = _sizeBucketExportLabel(safeEdge);
    const exportPhrase = _sizeBucketExportPhrase(safeEdge);

    if (stopBtn) stopBtn.style.display = 'none';
    if (progressArea) progressArea.classList.remove('hidden');
    if (resultCard) resultCard.classList.add('is-loading');
    if (resultLoader) resultLoader.classList.remove('hidden');
    if (resImg) resImg.classList.add('img-loading');
    if (origImg) origImg.classList.add('img-loading');
    if (Boolean(options.scroll)) {
        requestWorkflowScroll('viewer', { source: 'finalize_progress', afterFrame: true });
    }

    const pctOverride = Number(options.progressPct);
    const hasPctOverride = Number.isFinite(pctOverride);
    let pct = 8;
    let desc = `Preparing ${exportPhrase}...`;
    if (stateValue === 'queued') {
        pct = 15;
        desc = `${exportLabel} queued...`;
    } else if (stateValue === 'pending_payment') {
        pct = 30;
        desc = `Waiting for payment confirmation for ${exportPhrase}...`;
    } else if (stateValue === 'running') {
        pct = 62;
        desc = `Rendering ${exportPhrase}...`;
    } else if (stateValue === 'complete') {
        pct = 100;
        desc = `${exportLabel} ready.`;
    } else if (stateValue === 'failed' || stateValue === 'cancelled') {
        pct = 0;
        desc = `${exportLabel} failed.`;
    }
    if (hasPctOverride) {
        pct = Math.max(0, Math.min(100, pctOverride));
    }

    if (stepLabel) stepLabel.innerText = exportLabel;
    if (stepPct) stepPct.innerText = `${pct}%`;
    if (stepDesc) stepDesc.innerText = desc;
    if (bar) {
        bar.className = 'progress-fill active-build';
        bar.style.width = `${pct}%`;
    }
}

function _clearFinalizeProgressUi() {
    const progressArea = document.getElementById('progressArea');
    const resultCard = document.getElementById('resultCard');
    const resultLoader = document.getElementById('resultLoader');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    if (progressArea) progressArea.classList.add('hidden');
    if (resultCard) resultCard.classList.remove('is-loading');
    if (resultLoader) resultLoader.classList.add('hidden');
    if (resImg) resImg.classList.remove('img-loading');
    if (origImg) origImg.classList.remove('img-loading');
}

function _getHistoryJob(jobId) {
    if (!jobId) return null;
    return (state.history || []).find((job) => String(job.job_id || '') === String(jobId)) || null;
}

function _getActiveHistoryJob() {
    return _getHistoryJob(_activeJobId());
}

function _normalizeFinalizeRuns(job) {
    const src = Array.isArray(job?.finalize_runs) ? job.finalize_runs : [];
    return src
        .map((run) => ({
            final_run_id: String(run?.final_run_id || ''),
            status: String(run?.status || ''),
            long_edge_px: Number(run?.long_edge_px || 0),
            created_at: String(run?.created_at || ''),
        }))
        .filter((run) => run.final_run_id && run.long_edge_px > 0);
}

function _syncHistoryFinalizeBadge(jobId) {
    const safeJobId = String(jobId || '');
    if (!safeJobId) return;
    const historyJob = _getHistoryJob(safeJobId)
        || (state.selectedHistoryJob && String(state.selectedHistoryJob.job_id || '') === safeJobId
            ? state.selectedHistoryJob
            : null);
    const runs = _normalizeFinalizeRuns(historyJob || {});
    const readySizes = Array.from(
        new Set(
            runs
                .filter((run) => String(run.status || '') === 'complete')
                .map((run) => Number(run.long_edge_px || 0))
                .filter((edge) => Number.isFinite(edge) && edge > DRAFT_DIRECT_DOWNLOAD_EDGE)
        )
    ).sort((a, b) => a - b);
    const maxReady = readySizes.length ? readySizes[readySizes.length - 1] : 0;

    const items = Array.from(document.querySelectorAll('.history-item[data-job-id]'));
    const item = items.find((el) => String(el.getAttribute('data-job-id') || '') === safeJobId);
    if (!item) return;
    const tile = item.querySelector('.history-tile');
    if (!tile) return;

    tile.querySelectorAll('.history-finalize-badge').forEach((badge) => badge.remove());
    tile.classList.remove('has-finalize-badge');
    if (maxReady <= DRAFT_DIRECT_DOWNLOAD_EDGE) return;

    const badge = document.createElement('div');
    badge.className = 'history-finalize-badge';
    badge.textContent = _sizeBucketLabel(maxReady);
    badge.title = `Export ready: ${readySizes.join(', ')} px`;
    tile.classList.add('has-finalize-badge');
    tile.appendChild(badge);
}

function _rememberFinalizeRun({ finalRunId, size, status }) {
    const jobId = _activeJobId();
    if (!jobId || !finalRunId || !size) return;

    const applyToJob = (job) => {
        if (!job) return;
        const runs = _normalizeFinalizeRuns(job);
        const idx = runs.findIndex((run) => run.final_run_id === finalRunId);
        const nowIso = new Date().toISOString();
        const nextRun = {
            final_run_id: String(finalRunId),
            status: String(status || 'queued'),
            long_edge_px: Number(size || 0),
            created_at: (idx >= 0 ? runs[idx].created_at : nowIso) || nowIso,
        };
        if (idx >= 0) {
            runs[idx] = { ...runs[idx], ...nextRun };
        } else {
            runs.unshift(nextRun);
        }
        job.finalize_runs = runs;
    };

    const historyJob = _getHistoryJob(jobId);
    applyToJob(historyJob);
    if (state.selectedHistoryJob && String(state.selectedHistoryJob.job_id || '') === String(jobId)) {
        applyToJob(state.selectedHistoryJob);
    }
    _syncHistoryFinalizeBadge(jobId);
}

function _findCompleteFinalizeRun(job, size) {
    const safeSize = Number(size || 0);
    const runs = _normalizeFinalizeRuns(job);
    return runs.find((run) => run.long_edge_px === safeSize && run.status === 'complete') || null;
}

function _findLatestFinalizeRun(job, size) {
    const safeSize = Number(size || 0);
    const runs = _normalizeFinalizeRuns(job);
    return runs.find((run) => run.long_edge_px === safeSize) || null;
}

function _legacyDraftCustomSize(config, historyJob = null) {
    const configDraftMode = String((config || {}).draft_mode || '').toLowerCase();
    const historyDraftMode = String((historyJob || {}).draft_mode || '').toLowerCase();
    const draftMode = configDraftMode || historyDraftMode;
    if (draftMode !== 'legacy_highres') return 0;
    const w = Number((config || {}).w_final || (historyJob || {}).output_width || 0);
    const h = Number((config || {}).h_final || (historyJob || {}).output_height || 0);
    const edge = Math.max(w, h);
    if (!Number.isFinite(edge) || edge <= 0) return 0;
    return Math.round(edge);
}

function _closeDownloadModal() {
    const modal = document.getElementById('downloadModal');
    document.body.classList.remove('download-modal-open');
    if (_releaseDownloadModalScrollLock) {
        _releaseDownloadModalScrollLock();
        _releaseDownloadModalScrollLock = null;
        API.logUiFlow('result', 'download_modal_scroll_lock', 'released', {
            active_locks: getPageScrollLockCount(),
            modal: 'download',
        }).catch(() => {});
    }
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
}

function _openDownloadModal() {
    const modal = document.getElementById('downloadModal');
    if (!modal) return;
    document.body.classList.add('download-modal-open');
    if (!_releaseDownloadModalScrollLock) {
        _releaseDownloadModalScrollLock = acquirePageScrollLock();
        API.logUiFlow('result', 'download_modal_scroll_lock', 'acquired', {
            active_locks: getPageScrollLockCount(),
            modal: 'download',
        }).catch(() => {});
    }
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
}

function _bindDownloadModal() {
    if (_downloadModalBound) return;
    _downloadModalBound = true;

    const modal = document.getElementById('downloadModal');
    const closeBtn = document.getElementById('downloadModalClose');
    if (!modal) return;

    if (closeBtn) {
        closeBtn.addEventListener('click', () => _closeDownloadModal());
    }
    const backdrop = modal.querySelector('.modal-backdrop');
    if (backdrop) {
        backdrop.addEventListener('click', () => _closeDownloadModal());
    }
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (modal.classList.contains('hidden')) return;
        _closeDownloadModal();
    });
}

function _renderDownloadOptionContent(btn, option) {
    const head = document.createElement('div');
    head.className = 'download-option-head';

    const title = document.createElement('span');
    title.className = 'download-option-title';
    title.textContent = String(option?.displayTitle || option?.label || '');
    head.appendChild(title);

    const status = document.createElement('span');
    status.className = 'download-option-status';
    status.textContent = String(option?.displayStatus || '');
    if (status.textContent) head.appendChild(status);

    const usage = document.createElement('span');
    usage.className = 'download-option-sub';
    usage.textContent = String(option?.displayUsage || '');

    const longSide = document.createElement('span');
    longSide.className = 'download-option-size';
    longSide.textContent = String(option?.displayLongSide || '');

    btn.innerHTML = '';
    btn.appendChild(head);
    if (usage.textContent) btn.appendChild(usage);
    if (longSide.textContent) btn.appendChild(longSide);
}

function _renderDownloadUpsellCard(entitlements = {}) {
    const mount = document.getElementById('downloadModalUpsell');
    if (!mount) return;

    const tier = _currentTier(entitlements);
    if (tier !== 'free') {
        mount.classList.add('hidden');
        mount.innerHTML = '';
        return;
    }

    mount.innerHTML = `
        <div class="download-upsell-title">Want more exports at the lower price?</div>
        <div class="download-upsell-copy">
            Paid plan is the best for frequent exporters: 30x Medium and 1x Large exports per month included in price, along with other discounts. Priority queue and bigger libraries.
        </div>
        <div class="download-upsell-actions">
            <button type="button" class="btn secondary download-upsell-btn" data-upsell-compare>Compare plans</button>
        </div>
    `;
    mount.classList.remove('hidden');
    mount.setAttribute('role', 'button');
    mount.setAttribute('tabindex', '0');
    mount.setAttribute('aria-label', 'Compare plans');

    const compareBtn = mount.querySelector('[data-upsell-compare]');
    const openCompare = async (trigger = 'card_click') => {
        if (compareBtn && compareBtn.disabled) return;
        if (compareBtn) compareBtn.disabled = true;
        _logUpsellAction(UPSELL_SURFACE_DOWNLOAD_MODAL, 'compare_click', {
            source: 'download_modal',
            trigger,
        });
        try {
            const result = await openPlanComparisonModal({
                source: 'download_modal_banner',
                preferredPlan: 'paid',
                closeAction: 'back_to_download',
                returnContext: {
                    kind: 'subscription',
                    job_id: _activeJobId(),
                    long_edge_px: 0,
                    source: 'download_modal_banner',
                },
            });
            _logUpsellAction(UPSELL_SURFACE_DOWNLOAD_MODAL, String((result && result.action) || 'close'), {
                source: 'download_modal',
                trigger,
            });
        } finally {
            if (compareBtn) compareBtn.disabled = false;
        }
    };

    mount.onclick = (event) => {
        if (event && event.target && event.target.closest && event.target.closest('[data-upsell-compare]')) {
            return;
        }
        openCompare('card_click');
    };
    mount.onkeydown = (event) => {
        if (!event) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCompare('card_keyboard');
        }
    };

    if (compareBtn) {
        compareBtn.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await openCompare('button_click');
        });
    }
}

async function _downloadFinalizeReadyRun(run, size) {
    const finalRunId = String(run?.final_run_id || '');
    if (!finalRunId) {
        showToast('Export unavailable', 'Export run id missing.', 2800);
        return;
    }
    const jobId = _activeJobId();
    try {
        API.logEvent('finalize_redownload_requested', {
            job_id: jobId,
            final_run_id: finalRunId,
            long_edge_px: Number(size || 0),
        }).catch(() => {});
        const statusToast = showToast('Checking saved export', `Securing ${_sizeBucketExportPhrase(size)} download link...`, 0);
        let status = null;
        try {
            status = await API.finalizeStatus(finalRunId);
        } finally {
            if (statusToast) statusToast.close();
        }
        const stateVal = String(status?.status || '');
        const edge = Number(status?.long_edge_px || size || 0);
        _rememberFinalizeRun({ finalRunId, size: edge || size, status: stateVal || 'queued' });
        _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
        if (stateVal !== 'complete' || !status?.download_url) {
            showToast('Export not ready', 'Export is still processing. Try again shortly.', 3200);
            return;
        }
        await _downloadWithToasts(
            status.download_url,
            _downloadFilename(edge || size, finalRunId),
            {
                pendingTitle: `Preparing ${_sizeBucketExportLabel(edge || size || 0)} download`,
                pendingMessage: 'Preparing saved export file...',
                successTitle: 'Download started',
                successMessage: 'Export download started.',
                flow: 'finalize_redownload',
                transport: status?.download_transport || 'blob',
            },
        );
        API.logUiFlow('result', 'download', 'ok', {
            job_id: jobId,
            channel: 'finalize',
            long_edge_px: Number(edge || size || 0),
        }).catch(() => {});
        API.logEvent('finalize_redownload_started', {
            job_id: jobId,
            final_run_id: finalRunId,
            long_edge_px: Number(edge || size || 0),
        }).catch(() => {});
    } catch (e) {
        const msg = (e && e.message) ? e.message : 'Could not start export download.';
        showToast('Download unavailable', msg, 3200);
    }
}

async function _pollFinalizeRunUntilReady(finalRunId, size) {
    const safeRunId = String(finalRunId || '');
    if (!safeRunId) return false;
    if (_activeFinalizePolls.has(safeRunId)) {
        showToast('Export in progress', 'Already tracking this export run.', 2000);
        return false;
    }
    _activeFinalizePolls.add(safeRunId);

    const timeoutMs = 6 * 60 * 1000;
    const intervalMs = 2000;
    const startedAt = Date.now();
    try {
        while (Date.now() - startedAt < timeoutMs) {
            let status = null;
            try {
                status = await API.finalizeStatus(safeRunId);
            } catch (_err) {
                await new Promise((resolve) => setTimeout(resolve, intervalMs));
                continue;
            }
            if (!status) {
                await new Promise((resolve) => setTimeout(resolve, intervalMs));
                continue;
            }

            const stateVal = String(status.status || '');
            const runSize = Number(status.long_edge_px || size || 0);
            const progressPct = _nextFinalizeProgressPct(safeRunId, stateVal);
            _rememberFinalizeRun({
                finalRunId: safeRunId,
                size: runSize,
                status: stateVal || 'queued',
            });
            _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
            _setFinalizeProgressUi(stateVal, runSize || size, { progressPct });

            if (stateVal === 'complete') {
                _clearFinalizeProgressForRun(safeRunId);
                await _refreshFinalizeOptions(_activeJobId());
                _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
                _clearFinalizeProgressUi();
                if (status.download_url) {
                    await _downloadWithToasts(
                        status.download_url,
                        _downloadFilename(runSize || size || 0, safeRunId),
                    {
                            pendingTitle: `Starting ${_sizeBucketExportLabel(runSize || size || 0)} download`,
                            pendingMessage: 'Export is ready. Preparing download...',
                            successTitle: 'Export download started',
                            successMessage: 'Your export download has started.',
                            flow: 'finalize_download',
                            transport: status?.download_transport || 'blob',
                        },
                    );
                    API.logUiFlow('result', 'download', 'ok', {
                        job_id: _activeJobId(),
                        channel: 'finalize',
                        long_edge_px: Number(runSize || size || 0),
                    }).catch(() => {});
                    API.logEvent('finalize_download_started', {
                        job_id: _activeJobId(),
                        final_run_id: safeRunId,
                        long_edge_px: Number(runSize || size || 0),
                    }).catch(() => {});
                    return true;
                }
                showToast('Export ready', `${_sizeBucketExportLabel(runSize || size || 0)} is complete.`, 2600);
                return true;
            }
            if (stateVal === 'failed' || stateVal === 'cancelled') {
                showToast('Export failed', status.error_details || 'Try again.', 4200);
                _clearFinalizeProgressForRun(safeRunId);
                _clearFinalizeProgressUi();
                return false;
            }

            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
        showToast('Export running', 'You can continue working; check back in history soon.', 4200);
        _clearFinalizeProgressForRun(safeRunId);
        _clearFinalizeProgressUi();
        return false;
    } finally {
        _activeFinalizePolls.delete(safeRunId);
    }
}

export async function reconcileFinalizeAfterCheckout(context, options = {}) {
    const normalized = _normalizeBillingReturnContext(context);
    if (!normalized) {
        return { ok: false, status: 'invalid_context', payment_required: false, final_run_id: '' };
    }
    const jobId = String(normalized.job_id || '');
    const longEdgePx = Number(normalized.long_edge_px || 0);
    const timeoutMs = Math.max(5000, Number(options.timeoutMs || BILLING_RECONCILE_TIMEOUT_MS));
    const intervalMs = Math.max(1000, Number(options.intervalMs || BILLING_RECONCILE_INTERVAL_MS));
    const autoPoll = Boolean(options.autoPoll);
    const startedAt = Date.now();
    let lastKnown = null;

    API.logEvent('billing_return_reconcile_started', {
        job_id: jobId,
        long_edge_px: longEdgePx,
        final_run_id: String(normalized.final_run_id || ''),
    }).catch(() => {});

    while (Date.now() - startedAt < timeoutMs) {
        let started = null;
        try {
            started = await API.finalizeStart(jobId, longEdgePx);
        } catch (_err) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
            continue;
        }
        if (!started) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
            continue;
        }

        const runId = String(started.final_run_id || normalized.final_run_id || '');
        const stateVal = String(started.status || (started.payment_required ? 'pending_payment' : 'queued'));
        if (runId) {
            _rememberFinalizeRun({ finalRunId: runId, size: longEdgePx, status: stateVal });
            _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
        }

        if (started.payment_required) {
            lastKnown = {
                payment_required: true,
                checkout_url: String(started.checkout_url || normalized.checkout_url || ''),
                final_run_id: runId,
                status: stateVal || 'pending_payment',
            };
            if (runId) {
                const pct = _nextFinalizeProgressPct(runId, 'pending_payment');
                _setFinalizeProgressUi('pending_payment', longEdgePx, { progressPct: pct });
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
            continue;
        }

        _clearFinalizeProgressUi();
        clearSavedBillingReturnContext();
        API.logEvent('billing_return_reconcile_result', {
            job_id: jobId,
            long_edge_px: longEdgePx,
            final_run_id: runId,
            result: 'confirmed',
            status: stateVal || 'queued',
            duration_ms: Date.now() - startedAt,
        }).catch(() => {});

        if (autoPoll && runId && ['queued', 'running'].includes(String(stateVal || '').toLowerCase())) {
            await _pollFinalizeRunUntilReady(runId, longEdgePx);
        }
        return {
            ok: true,
            status: stateVal || 'queued',
            payment_required: false,
            final_run_id: runId,
            checkout_url: '',
        };
    }

    _clearFinalizeProgressUi();
    const pendingCheckoutUrl = String((lastKnown && lastKnown.checkout_url) || normalized.checkout_url || '');
    API.logEvent('billing_return_reconcile_result', {
        job_id: jobId,
        long_edge_px: longEdgePx,
        final_run_id: String((lastKnown && lastKnown.final_run_id) || normalized.final_run_id || ''),
        result: 'pending_timeout',
        duration_ms: Date.now() - startedAt,
    }).catch(() => {});
    return {
        ok: false,
        status: String((lastKnown && lastKnown.status) || 'pending_payment'),
        payment_required: true,
        final_run_id: String((lastKnown && lastKnown.final_run_id) || normalized.final_run_id || ''),
        checkout_url: pendingCheckoutUrl,
    };
}

async function _startExportFlow(option, baseDownloadUrl) {
    const size = Number(option?.size || 0);
    const kind = String(option?.kind || 'finalize');
    const jobId = _activeJobId();
    const hasRunningRun = Boolean(option?.run && ['queued', 'running'].includes(String(option.run.status || '').toLowerCase()));
    const flowKey = `${jobId}:${size}`;
    if (!jobId) {
        showToast('Export unavailable', 'Open a mosaic first.', 2400);
        return;
    }

    if (kind === 'draft_direct' || size <= Number(DRAFT_DIRECT_DOWNLOAD_EDGE)) {
        if (!baseDownloadUrl) {
            showToast('Export unavailable', 'Small download URL is missing.', 2600);
            return;
        }
        _closeDownloadModal();
        const directUrl = addLongEdgeParam(addDownloadFlag(baseDownloadUrl), size || DRAFT_DIRECT_DOWNLOAD_EDGE);
        await _downloadWithToasts(
            directUrl,
            _downloadFilename(size || DRAFT_DIRECT_DOWNLOAD_EDGE),
            {
                pendingTitle: `Preparing ${_sizeBucketLabel(size || DRAFT_DIRECT_DOWNLOAD_EDGE)} download`,
                pendingMessage: 'Preparing download...',
                successTitle: `${_sizeBucketLabel(size || DRAFT_DIRECT_DOWNLOAD_EDGE)} download started`,
                successMessage: `${_sizeBucketLabel(size || DRAFT_DIRECT_DOWNLOAD_EDGE)} download started.`,
                flow: 'draft_download',
            },
        );
        API.logUiFlow('result', 'download', 'ok', {
            job_id: jobId,
            channel: 'draft',
            long_edge_px: Number(size || DRAFT_DIRECT_DOWNLOAD_EDGE),
        }).catch(() => {});
        return;
    }

    if (kind === 'finalize_ready' && option?.run) {
        _closeDownloadModal();
        await _downloadFinalizeReadyRun(option.run, size);
        _clearFinalizeProgressUi();
        return;
    }

    if (kind === 'finalize' && hasRunningRun) {
        _closeDownloadModal();
        const runningRunId = String(option.run.final_run_id || '');
        const startPct = _nextFinalizeProgressPct(runningRunId, String(option.run.status || 'queued'));
        _setFinalizeProgressUi(String(option.run.status || 'queued'), size, { scroll: true, progressPct: startPct });
        await _pollFinalizeRunUntilReady(runningRunId, size);
        return;
    }

    if (_activeFinalizeFlowStarts.has(flowKey)) {
        showToast('Export in progress', 'This export is already starting. Please wait.', 2200);
        return;
    }
    _activeFinalizeFlowStarts.add(flowKey);
    try {
        API.logEvent('finalize_create_flow_started', {
            job_id: jobId,
            long_edge_px: size,
            confirm_kind: String(option?.confirmKind || ''),
        }).catch(() => {});
        const preflight = await API.finalizePreflight(jobId, size);
        if (!preflight || preflight.ok === false) {
            const missing = Array.isArray(preflight?.missing_tiles) ? preflight.missing_tiles : [];
            if (missing.length) {
                showToast('Export blocked', `Missing tile dependencies: ${missing.slice(0, 3).join(', ')}`, 4800);
            } else {
                const warnings = Array.isArray(preflight?.quality_warnings) ? preflight.quality_warnings : [];
                showToast('Export unavailable', warnings[0] || 'Regenerate this draft before creating an export.', 4200);
            }
            return;
        }
        _closeDownloadModal();
        const confirmKindRaw = String(option?.confirmKind || '').trim().toLowerCase();
        const confirmKind = confirmKindRaw || (Boolean(option?.locked) ? 'checkout_payment' : 'none');
        if (confirmKind === 'checkout_payment' && _currentTier() === 'free') {
            const shouldShowUpsell = _consumeUpsellSurface(UPSELL_SURFACE_DOWNLOAD_LOCKED, {
                source: 'download_locked_click',
                size_px: size,
                job_id: jobId,
            });
            if (shouldShowUpsell) {
                const compareResult = await openPlanComparisonModal({
                    source: 'download_locked_click',
                    preferredPlan: 'paid',
                    closeAction: 'continue_with_download',
                    returnContext: {
                        kind: 'subscription',
                        job_id: jobId,
                        long_edge_px: size,
                        source: 'download_locked_click',
                    },
                });
                _logUpsellAction(UPSELL_SURFACE_DOWNLOAD_LOCKED, String((compareResult && compareResult.action) || 'close'), {
                    source: 'download_locked_click',
                    size_px: size,
                    job_id: jobId,
                });
                if (compareResult && compareResult.action === 'checkout') {
                    return;
                }
            }
        }
        if (confirmKind === 'checkout_payment') {
            const exportSizeBucket = _sizeBucketLabel(size);
            const confirmed = await openConfirmModal({
                title: `Create a ${exportSizeBucket} export.`,
                message: `A new export will be generated with the size of ${size}px. Payment is required first.\nContinue and you will be redirected to checkout.\nThis purchase applies only to this current mosaic and this selected size.`,
                confirmText: 'Go to checkout',
                cancelText: 'Cancel',
            });
            if (!confirmed) return;
        } else if (confirmKind === 'included_quota') {
            const exportSizeBucket = _sizeBucketLabel(size);
            const confirmed = await openConfirmModal({
                title: `Create a ${exportSizeBucket} export.`,
                message: `A new export will be generated with the size of ${size}px. This will count against your monthly quota and cannot be undone.`,
                confirmText: 'Create export',
                cancelText: 'Cancel',
            });
            if (!confirmed) return;
        }

        const started = await API.finalizeStart(jobId, size);
        if (!started) {
            showToast('Export unavailable', 'Could not start export.', 2800);
            return;
        }
        if (started.payment_required && started.checkout_url) {
            _saveBillingReturnContext({
                kind: 'finalize',
                user_id: _billingContextCurrentUserId(),
                job_id: jobId,
                long_edge_px: size,
                final_run_id: String(started.final_run_id || ''),
                checkout_url: String(started.checkout_url || ''),
                checkout_intent_id: String(started.checkout_intent_id || ''),
            });
            showToast('Redirecting to checkout', `${_sizeBucketExportLabel(size)} requires payment.`, 2200);
            API.logEvent('finalize_checkout_redirect', {
                job_id: jobId,
                long_edge_px: size,
            }).catch(() => {});
            window.location.href = started.checkout_url;
            return;
        }
        if (started.payment_required && !started.checkout_url) {
            _saveBillingReturnContext({
                kind: 'finalize',
                user_id: _billingContextCurrentUserId(),
                job_id: jobId,
                long_edge_px: size,
                final_run_id: String(started.final_run_id || ''),
                checkout_url: '',
                checkout_intent_id: String(started.checkout_intent_id || ''),
            });
            showToast('Checkout unavailable', 'Payment is required but checkout link is missing. Please try again.', 3600);
            return;
        }

        const finalRunId = String(started.final_run_id || '');
        if (!finalRunId) {
            showToast('Export unavailable', 'Export run id missing.', 3200);
            return;
        }
        _rememberFinalizeRun({ finalRunId, size, status: String(started.status || 'queued') });
        await _refreshFinalizeOptions(jobId);
        _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
        API.logEvent('finalize_first_time_started', {
            job_id: jobId,
            final_run_id: finalRunId,
            long_edge_px: size,
            status: String(started.status || 'queued'),
        }).catch(() => {});

        if (started.status === 'complete') {
            const status = await API.finalizeStatus(finalRunId);
            _rememberFinalizeRun({
                finalRunId,
                size: Number(status?.long_edge_px || size || 0),
                status: String(status?.status || 'complete'),
            });
            await _refreshFinalizeOptions(jobId);
            _renderDownloadButtons(_activeDownloadBaseUrl, _activeDownloadConfig || {});
            _clearFinalizeProgressForRun(finalRunId);
            _clearFinalizeProgressUi();
            if (status && status.download_url) {
                await _downloadWithToasts(
                    status.download_url,
                    _downloadFilename(Number(status.long_edge_px || size || 0), finalRunId),
                    {
                        pendingTitle: `Starting ${_sizeBucketExportLabel(Number(status.long_edge_px || size || 0))} download`,
                        pendingMessage: 'Export completed. Preparing download...',
                        successTitle: 'Export download started',
                        successMessage: 'Your export download has started.',
                        flow: 'finalize_download',
                        transport: status?.download_transport || 'blob',
                    },
                );
                API.logUiFlow('result', 'download', 'ok', {
                    job_id: jobId,
                    channel: 'finalize',
                    long_edge_px: Number(status.long_edge_px || size || 0),
                }).catch(() => {});
                API.logEvent('finalize_download_started', {
                    job_id: jobId,
                    final_run_id: finalRunId,
                    long_edge_px: Number(status.long_edge_px || size || 0),
                }).catch(() => {});
                return;
            }
            showToast('Export ready', `${_sizeBucketExportLabel(size)} is complete.`, 2400);
            return;
        }

        const startPct = _nextFinalizeProgressPct(finalRunId, String(started.status || 'queued'));
        _setFinalizeProgressUi(String(started.status || 'queued'), size, { scroll: true, progressPct: startPct });
        showToast('Export started', `Rendering ${_sizeBucketExportPhrase(size)}...`, 2200);
        await _pollFinalizeRunUntilReady(finalRunId, size);
    } catch (e) {
        const msg = (e && e.message) ? e.message : 'Could not start export.';
        _clearFinalizeProgressUi();
        showToast('Export unavailable', msg, 3200);
    } finally {
        _activeFinalizeFlowStarts.delete(flowKey);
    }
}

function _renderDownloadButtons(baseDownloadUrl, config = {}) {
    _activeDownloadBaseUrl = baseDownloadUrl || '';
    _activeDownloadConfig = config || {};

    _bindDownloadModal();
    const container = document.getElementById('downloadModalOptions');
    const hint = document.getElementById('downloadModalHint');
    const downloadBtn = document.getElementById('downloadBtn');
    if (!container || !downloadBtn) return;
    const dpi = PRINT_DPI_DEFAULT;

    const entitlements = _getEntitlements();
    const downloads = entitlements.downloads || {};
    const includedBuckets = _includedExportBuckets(entitlements);
    const includedMax = Number(downloads.included_max_long_edge_px || 0);
    const historyJob = _getActiveHistoryJob();
    const activeJobId = _activeJobId();
    const legacyDraftSize = _legacyDraftCustomSize(config, historyJob);
    const aspectCtx = _resolveAspectContext(config, historyJob);
    _renderDownloadUpsellCard(entitlements);

    container.innerHTML = '';
    const options = [];
    const includedRemainingLabelForSize = (size) => {
        const bucket = includedBuckets[Number(size || 0)] || null;
        if (!bucket || Number(bucket.total || 0) <= 0) return '';
        return `${Math.max(0, Number(bucket.remaining || 0))} left`;
    };
    const includedPlanStatus = (size) => {
        const bucket = includedBuckets[Number(size || 0)] || null;
        if (bucket && Number(bucket.total || 0) > 0) {
            return `Included in plan • ${includedRemainingLabelForSize(size) || '0 left'}`;
        }
        return 'Included in plan';
    };
    const withVisuals = (option, statusText, statusClass = '') => {
        const size = Number(option.size || 0);
        return {
            ...option,
            displayTitle: String(option.displayTitle || _sizeBucketLabel(size)),
            displayUsage: String(option.displayUsage || _sizeBucketUseText(size)),
            displayLongSide: _longEdgeSummary(size, dpi),
            displayStatus: String(statusText || ''),
            statusClass: String(statusClass || ''),
        };
    };

    if (legacyDraftSize > 0 && legacyDraftSize !== DRAFT_DIRECT_DOWNLOAD_EDGE) {
        options.push(withVisuals({
            size: legacyDraftSize,
            kind: 'draft_direct',
            label: `Download draft ${legacyDraftSize}px (Legacy)`,
            title: `Download the original legacy draft output at ${legacyDraftSize}px.`,
            locked: false,
            displayTitle: 'Legacy',
            displayUsage: 'legacy draft',
        }, 'Instant', ''));
    }

    EXPORT_SIZES.forEach((size) => {
        const readyRun = _findCompleteFinalizeRun(historyJob, size);
        const latestRun = _findLatestFinalizeRun(historyJob, size);
        const backendOption = (_finalizeOptionsState.jobId === String(activeJobId || ''))
            ? _getFinalizeOptionForSize(size)
            : null;
        const includedBucket = includedBuckets[Number(size || 0)] || null;
        let option = {
            size,
            kind: 'finalize',
            label: `Create an export ${size}px`,
            title: `Create and download ${size}px export output.`,
            locked: false,
            run: null,
            confirmKind: 'none',
            displayTitle: _sizeBucketLabel(size),
        };

        if (size === DRAFT_DIRECT_DOWNLOAD_EDGE) {
            option = withVisuals({
                size,
                kind: 'draft_direct',
                label: `Download ${size}px`,
                title: `Instant draft download at ${size}px.`,
                locked: false,
                run: null,
                confirmKind: 'none',
                displayTitle: 'Small',
                displayUsage: 'web/phone',
            }, 'Ready to download', 'is-included');
        } else if (backendOption && backendOption.state) {
            const stateKey = String(backendOption.state || '').trim().toLowerCase();
            const backendRun = (
                String(backendOption.final_run_id || '')
                ? {
                    final_run_id: String(backendOption.final_run_id || ''),
                    status: String(backendOption.run_status || ''),
                    long_edge_px: Number(size || 0),
                }
                : null
            );
            const backendStatusText = String(backendOption.status_text || '').trim();
            const backendStatusClass = String(backendOption.status_class || '').trim();
            const isLocked = Boolean(backendOption.locked);
            const confirmKind = String(backendOption.confirm_kind || '').trim() || 'none';
            const fallbackPrice = _priceForSize(size, entitlements);
            const checkoutUrl = String(backendOption.checkout_url || '').trim();

            if (stateKey === 'ready_download') {
                option = withVisuals({
                    size,
                    kind: 'finalize_ready',
                    label: `Download ${size}px (Ready)`,
                    title: `${size}px export is ready. Click to download.`,
                    locked: false,
                    run: backendRun || readyRun || null,
                    confirmKind,
                }, backendStatusText || 'Ready to download', backendStatusClass || 'is-included');
            } else if (stateKey === 'rendering') {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Create an export ${size}px (Rendering)`,
                    title: `Export is already rendering. Click to check status and download when ready.`,
                    locked: false,
                    run: backendRun || latestRun || null,
                    confirmKind: 'none',
                }, backendStatusText || 'Rendering', backendStatusClass || '');
            } else if (stateKey === 'ready_generate') {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Generate export ${size}px`,
                    title: `Payment is confirmed. Click to generate and download ${size}px export.`,
                    locked: false,
                    run: backendRun || latestRun || null,
                    confirmKind: 'none',
                }, backendStatusText || 'Ready to generate', backendStatusClass || 'is-ready');
            } else if (stateKey === 'included') {
                const backendIncludedTotal = Number(backendOption.included_total || backendOption.monthly_total || 0);
                const defaultLabel = (confirmKind === 'included_quota')
                    ? `Create an export ${size}px (Use 1 of ${Math.max(0, backendIncludedTotal)})`
                    : `Create an export ${size}px (Included)`;
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: defaultLabel,
                    title: (confirmKind === 'included_quota')
                        ? `Uses one included monthly print export.`
                        : `Included in your current plan.`,
                    locked: false,
                    run: null,
                    confirmKind,
                }, backendStatusText || includedPlanStatus(size), backendStatusClass || 'is-included');
            } else {
                const backendPrice = Number(backendOption.price_cents || fallbackPrice || 0);
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Unlock export ${size}px for ${_formatUsd(backendPrice)}`,
                    title: checkoutUrl
                        ? `Continue checkout for this export, or start a new checkout.`
                        : `One-time unlock for this job and size.`,
                    locked: Boolean(isLocked),
                    run: backendRun || null,
                    confirmKind: confirmKind || 'checkout_payment',
                    checkoutUrl,
                }, backendStatusText || `Unlock ${_formatUsd(backendPrice)}`, backendStatusClass || 'is-locked');
            }
        } else {
            const hasPaidIntent = _hasPaidIntentForJobSize(activeJobId, size);
            if (readyRun) {
                option = withVisuals({
                    size,
                    kind: 'finalize_ready',
                    label: `Download ${size}px (Ready)`,
                    title: `${size}px export is ready. Click to download.`,
                    locked: false,
                    run: readyRun,
                    confirmKind: 'none',
                }, 'Ready to download', 'is-included');
            } else if (latestRun && ['queued', 'running'].includes(String(latestRun.status || ''))) {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Create an export ${size}px (Rendering)`,
                    title: `Export is already rendering. Click to check status and download when ready.`,
                    locked: false,
                    run: latestRun,
                    confirmKind: 'none',
                }, 'Rendering', '');
            } else if (hasPaidIntent) {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Generate export ${size}px`,
                    title: `Payment is confirmed. Click to generate and download ${size}px export.`,
                    locked: false,
                    run: latestRun || null,
                    confirmKind: 'none',
                }, 'Ready to generate', 'is-ready');
            } else if (includedBucket && Number(includedBucket.total || 0) > 0 && Number(includedBucket.remaining || 0) > 0) {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Create an export ${size}px (Use 1 of ${Math.max(0, Number(includedBucket.total || 0))})`,
                    title: `Uses one included monthly print export.`,
                    locked: false,
                    run: null,
                    confirmKind: 'included_quota',
                }, includedPlanStatus(size), 'is-included');
            } else if (includedBucket && Number(includedBucket.total || 0) > 0 && Number(includedBucket.remaining || 0) <= 0) {
                const price = _priceForSize(size, entitlements);
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Unlock export ${size}px for ${_formatUsd(price)}`,
                    title: `Included plan quota is exhausted for this size.`,
                    locked: true,
                    run: null,
                    confirmKind: 'checkout_payment',
                }, `0 left in plan • Unlock ${_formatUsd(price)}`, 'is-locked');
            } else if (size <= includedMax) {
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Create an export ${size}px (Included)`,
                    title: `Included in your current plan.`,
                    locked: false,
                    run: null,
                    confirmKind: 'none',
                }, 'Included in plan', 'is-included');
            } else {
                const price = _priceForSize(size, entitlements);
                option = withVisuals({
                    size,
                    kind: 'finalize',
                    label: `Unlock export ${size}px for ${_formatUsd(price)}`,
                    title: `One-time unlock for this job and size.`,
                    locked: true,
                    run: null,
                    confirmKind: 'checkout_payment',
                }, `Unlock ${_formatUsd(price)}`, 'is-locked');
            }
        }

        options.push(option);
    });

    options.forEach((option) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn download-option';
        _renderDownloadOptionContent(btn, option);
        btn.title = option.title;
        btn.classList.toggle('is-locked', Boolean(option.locked));
        if (option.statusClass) btn.classList.add(option.statusClass);
        btn.addEventListener('click', async () => {
            if (btn.disabled) return;
            const printSize = _computePrintSize(Number(option.size || 0), dpi, aspectCtx.ratio);
            API.logEvent('download_option_clicked', {
                job_id: _activeJobId(),
                mode: String(option.kind || ''),
                size_px: Number(option.size || 0),
                dpi,
                locked: Boolean(option.locked),
                status: String(option.displayStatus || ''),
                paper_match: _paperSummary(printSize),
            }).catch(() => {});
            btn.disabled = true;
            try {
                await _startExportFlow(option, baseDownloadUrl);
            } finally {
                btn.disabled = false;
            }
        });
        container.appendChild(btn);
    });

    if (hint) {
        hint.textContent = 'Print estimates are based on the mosaic long side at 300 DPI. The short side scales with your photo proportions.';
    }

    downloadBtn.onclick = () => {
        if (state.isAnonymous) {
            const jobId = _activeJobId();
            promptSignInForDownload(jobId);
            API.logUiFlow('result', 'download_gate', 'prompted', { job_id: jobId }).catch(() => {});
            return;
        }
        _openDownloadModal();
        document.dispatchEvent(new CustomEvent('onboarding:download_modal_open', {
            detail: {
                job_id: _activeJobId(),
            },
        }));
        API.logUiFlow('result', 'download_modal_open', 'ok', {
            job_id: _activeJobId(),
        }).catch(() => {});
    };
}

function getViewerEmpty() {
    return document.getElementById('viewerEmpty');
}

function getViewerContent() {
    return document.getElementById('viewerContent');
}

function getCompareMaxWidth(container) {
    const body = container ? container.closest('.collapsible-card-body') : null;
    const host = body || (container ? container.parentElement : null);
    if (!host) return container ? container.clientWidth : window.innerWidth;
    const styles = window.getComputedStyle(host);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    const available = host.clientWidth - padLeft - padRight;
    if (Number.isFinite(available) && available > 0) return available;
    return host.clientWidth || (container ? container.clientWidth : window.innerWidth);
}

function setViewerEmptyText(message) {
    const textEl = document.getElementById('viewerEmptyText');
    if (textEl) {
        textEl.textContent = message;
        return;
    }
    const empty = getViewerEmpty();
    if (empty) empty.textContent = message;
}

function isCompareDebugEnabled() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.has('compare_debug')) {
            return params.get('compare_debug') !== '0';
        }
    } catch (e) {}
    return localStorage.getItem('compare_debug') === '1';
}

function updateCompareDebug() {
    const debug = document.getElementById('compareDebug');
    const container = document.getElementById('compareContainer');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    const overlay = document.getElementById('overlayDiv');
    if (!debug || !container || !resImg || !origImg) return;

    if (!isCompareDebugEnabled()) {
        debug.classList.add('hidden');
        return;
    }

    const hasAny = Boolean(resImg.src || origImg.src);
    debug.classList.toggle('hidden', !hasAny);
    if (!hasAny) return;

    const fmtSize = (w, h) => (w && h) ? `${w}x${h}` : '-';
    const fmtClient = (el) => el ? `${Math.round(el.clientWidth)}x${Math.round(el.clientHeight)}` : '-';
    const fmtSrc = (src) => {
        if (!src) return '-';
        try {
            const u = new URL(src, window.location.origin);
            if (u.origin === window.location.origin) {
                return `${u.pathname}${u.search}`;
            }
            return `${u.origin}${u.pathname}`;
        } catch (e) {
            return src.startsWith('blob:') ? 'blob:' : src;
        }
    };

    const overlayPct = (overlay && container && container.clientWidth)
        ? Math.round((overlay.clientWidth / container.clientWidth) * 100)
        : 0;

    const maxWidth = getCompareMaxWidth(container);
    const lines = [
        `container client: ${fmtClient(container)}`,
        `max width calc: ${Math.round(maxWidth)}px`,
        `res img: nat ${fmtSize(resImg.naturalWidth, resImg.naturalHeight)} | client ${fmtClient(resImg)}`,
        `orig img: nat ${fmtSize(origImg.naturalWidth, origImg.naturalHeight)} | client ${fmtClient(origImg)}`,
        `overlay width: ${overlayPct}%`,
        `res src: ${fmtSrc(resImg.dataset.displayUrl || resImg.src)}`,
        `orig src: ${fmtSrc(origImg.dataset.sourceUrl || origImg.src)}`,
    ];
    debug.textContent = lines.join('\n');
}

function _clearLivePreviewQueueState({ keepImage = true, keepBuildClasses = false } = {}) {
    const resultCard = document.getElementById('resultCard');
    if (resultCard) {
        if (!keepBuildClasses) {
            resultCard.classList.remove('has-live-preview');
            resultCard.classList.remove('build-preview-mode');
            resultCard.style.removeProperty('--build-frontier-pct');
        }
        resultCard.classList.remove('compare-intro-mode');
    }
    _livePreviewState.jobId = '';
    _livePreviewState.latestVersion = 0;
    _livePreviewState.queue = [];
    _livePreviewState.inflight = false;
    _livePreviewState.token += 1;
    _livePreviewState.lastFetchStartedAt = 0;
    if (_livePreviewState.abortController) {
        try {
            _livePreviewState.abortController.abort();
        } catch (_e) {}
        _livePreviewState.abortController = null;
    }
    if (!keepImage) {
        const resImg = document.getElementById('resImg');
        if (resImg && resImg.dataset.blobUrl) {
            try {
                URL.revokeObjectURL(resImg.dataset.blobUrl);
            } catch (_e) {}
            delete resImg.dataset.blobUrl;
        }
    }
}

function _userHeaderForImageFetch() {
    return {};
}

function _clampPct(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    if (numeric < 0) return 0;
    if (numeric > 100) return 100;
    return numeric;
}

function _sourceIdentity(raw) {
    const src = String(raw || '').trim();
    if (!src) return '';
    try {
        const url = new URL(src, window.location.origin);
        const params = new URLSearchParams(url.search || '');
        // Ignore only volatile cache-busting params but preserve identity params.
        ['v', 'ts', 't', '_'].forEach((key) => params.delete(key));
        const normalizedQuery = params.toString();
        return normalizedQuery
            ? `${url.origin}${url.pathname}?${normalizedQuery}`
            : `${url.origin}${url.pathname}`;
    } catch (_e) {
        try {
            const [base, queryRaw = ''] = src.split('?', 2);
            if (!queryRaw) return base;
            const params = new URLSearchParams(queryRaw);
            ['v', 'ts', 't', '_'].forEach((key) => params.delete(key));
            const normalizedQuery = params.toString();
            return normalizedQuery ? `${base}?${normalizedQuery}` : base;
        } catch (_inner) {
            return src;
        }
    }
}

function _primeHiddenSourcePreload(rawSrc = '') {
    const src = String(rawSrc || '').trim();
    if (!src) return Promise.resolve(false);
    const identity = _sourceIdentity(src);
    if (_sourcePreloadState.identity === identity && _sourcePreloadState.promise) {
        return _sourcePreloadState.promise;
    }
    _sourcePreloadState.identity = identity;
    _sourcePreloadState.src = src;
    _sourcePreloadState.ready = false;
    _sourcePreloadState.failed = false;
    const img = new Image();
    img.decoding = 'async';
    _sourcePreloadState.promise = new Promise((resolve) => {
        const finish = (ok) => {
            _sourcePreloadState.ready = Boolean(ok);
            _sourcePreloadState.failed = !ok;
            resolve(Boolean(ok));
        };
        img.onload = () => {
            if (typeof img.decode === 'function') {
                img.decode().catch(() => {}).finally(() => finish(true));
                return;
            }
            finish(true);
        };
        img.onerror = () => finish(false);
        img.src = src;
        if (img.complete && img.naturalWidth > 0) {
            if (typeof img.decode === 'function') {
                img.decode().catch(() => {}).finally(() => finish(true));
            } else {
                finish(true);
            }
        }
    });
    return _sourcePreloadState.promise;
}

function _animateOverlayWidth(overlay, fromPct, toPct, durationMs, token) {
    const safeOverlay = overlay;
    const from = _clampPct(fromPct);
    const to = _clampPct(toPct);
    const duration = Math.max(120, Number(durationMs || 0));
    return new Promise((resolve) => {
        const startTs = performance.now();
        const step = (ts) => {
            if (!safeOverlay || !safeOverlay.isConnected || token !== _compareIntroToken) {
                resolve(false);
                return;
            }
            const t = Math.max(0, Math.min(1, (ts - startTs) / duration));
            const eased = t * t * (3 - (2 * t));
            const current = from + ((to - from) * eased);
            safeOverlay.style.width = `${current}%`;
            if (t >= 1) {
                resolve(true);
                return;
            }
            window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
    });
}

async function _playCompareIntroSweep(overlay) {
    if (!overlay || !overlay.isConnected) return;
    _compareIntroToken += 1;
    const token = _compareIntroToken;
    overlay.style.width = '0%';
    const pass1 = await _animateOverlayWidth(overlay, 0, 100, 840, token);
    if (!pass1) return;
    if (token !== _compareIntroToken) return;
    await _animateOverlayWidth(overlay, 100, 50, 560, token);
}

function _prefersReducedMotion() {
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function _waitForPopoutTransition(node, timeoutMs = 520) {
    return new Promise((resolve) => {
        if (!node) {
            resolve();
            return;
        }
        let done = false;
        const settle = () => {
            if (done) return;
            done = true;
            node.removeEventListener('transitionend', onEnd);
            window.clearTimeout(timerId);
            resolve();
        };
        const onEnd = (event) => {
            if (!event || event.target !== node) return;
            if (!['left', 'top', 'width', 'height'].includes(String(event.propertyName || ''))) return;
            settle();
        };
        const timerId = window.setTimeout(settle, Math.max(220, Number(timeoutMs || 0)));
        node.addEventListener('transitionend', onEnd);
    });
}

function _computeViewerPopoutTargetRect(imageWidth = 0, imageHeight = 0) {
    const safeW = Math.max(1, Number(imageWidth || 1));
    const safeH = Math.max(1, Number(imageHeight || 1));
    const ratio = safeW / safeH;
    const margin = Math.max(12, Math.round(window.innerWidth * 0.02));
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
    const topInset = headerHeight + margin;
    const availW = Math.max(240, window.innerWidth - (margin * 2));
    const availH = Math.max(180, window.innerHeight - topInset - margin);
    let width = availW;
    let height = Math.round(width / ratio);
    if (height > availH) {
        height = availH;
        width = Math.round(height * ratio);
    }
    const left = Math.round((window.innerWidth - width) / 2);
    const top = Math.round(topInset + ((availH - height) / 2));
    return { left, top, width, height };
}

async function _runViewerPopoutAnimation() {
    if (_prefersReducedMotion()) return;
    const resImg = document.getElementById('resImg');
    const container = document.getElementById('compareContainer');
    if (!resImg || !container) return;
    if (!resImg.naturalWidth || !resImg.naturalHeight) return;

    const sourceRect = container.getBoundingClientRect();
    if (!Number.isFinite(sourceRect.width) || sourceRect.width < 24 || sourceRect.height < 24) return;

    const safeSrc = String(resImg.currentSrc || resImg.src || '').trim();
    if (!safeSrc) return;

    const runToken = ++_viewerPopoutToken;
    API.logUiFlow('result', 'viewer_popout', 'start', {
        job_id: _activeJobId(),
    }).catch(() => {});
    const shell = document.createElement('div');
    shell.className = 'viewer-popout-shell';
    shell.setAttribute('aria-hidden', 'true');

    const img = document.createElement('img');
    img.className = 'viewer-popout-image';
    img.alt = 'Mosaic popout preview';
    img.src = safeSrc;
    shell.appendChild(img);

    const applyRect = (rect) => {
        shell.style.left = `${Math.round(rect.left)}px`;
        shell.style.top = `${Math.round(rect.top)}px`;
        shell.style.width = `${Math.round(rect.width)}px`;
        shell.style.height = `${Math.round(rect.height)}px`;
    };

    applyRect(sourceRect);
    document.body.appendChild(shell);
    document.body.classList.add('viewer-popout-running');

    const targetRect = _computeViewerPopoutTargetRect(resImg.naturalWidth, resImg.naturalHeight);
    shell.getBoundingClientRect();
    shell.classList.add('is-expanded');
    applyRect(targetRect);
    await _waitForPopoutTransition(shell, 520);
    if (runToken !== _viewerPopoutToken) {
        shell.remove();
        document.body.classList.remove('viewer-popout-running');
        return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 460));
    applyRect(sourceRect);
    shell.classList.remove('is-expanded');
    await _waitForPopoutTransition(shell, 520);
    if (shell.isConnected) shell.remove();
    if (runToken === _viewerPopoutToken) {
        document.body.classList.remove('viewer-popout-running');
        API.logUiFlow('result', 'viewer_popout', 'ok', {
            job_id: _activeJobId(),
        }).catch(() => {});
    }
}

function _runDeferredBuildLayout(container, resImg, origImg, width, height) {
    const w = Number(width || 0);
    const h = Number(height || 0);
    if (!container || !resImg || !origImg || w <= 0 || h <= 0) return;
    const apply = () => {
        fitCompareToSize(container, resImg, origImg, w, h);
        updateCompareDebug();
    };
    apply();
    [120, 300, 650, 1100].forEach((delay) => {
        window.setTimeout(apply, delay);
    });
}

function _parseLivePreviewVersion(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return 0;
    return Math.floor(numeric);
}

function _extractLivePreviewPayload(detail = {}) {
    if (!JOB_MONITORING.LIVE_PREVIEW_ENABLED) return null;
    const raw = (detail && detail.raw) ? detail.raw : {};
    const phase = String(detail.phase || raw.phase || '').trim().toLowerCase();
    if (!LIVE_PREVIEW_PHASES.has(phase)) return null;
    const jobId = String(detail.jobId || raw.job_id || state.jobId || '').trim();
    if (!jobId) return null;

    const directUrl = String(detail.livePreviewUrl || raw.live_preview_url || '').trim();
    const key = String(detail.livePreviewKey || raw.live_preview_key || '').trim();
    const total = Number(detail.total ?? raw.total ?? 0);
    const current = Number(detail.current ?? raw.current ?? 0);
    const fallbackFrontierPct = (total > 0) ? ((current / total) * 100) : 0;
    const frontierPct = _clampPct(
        detail.livePreviewPct
        ?? raw.live_preview_pct
        ?? fallbackFrontierPct
    );
    const version = _parseLivePreviewVersion(
        detail.livePreviewVersion
        ?? raw.live_preview_version
        ?? raw.live_preview_updated_ts
        ?? raw.updated_ts
        ?? Date.now()
    );
    if (!directUrl && !key) return null;
    const url = directUrl || `/result-image/${jobId}?key=${encodeURIComponent(key)}&v=${version || Date.now()}`;
    return { jobId, phase, url, version, frontierPct };
}

async function _fetchLivePreviewFrame(url, signal) {
    const response = await fetch(url, { headers: _userHeaderForImageFetch(), signal });
    if (!response.ok) {
        throw new Error(`Live preview HTTP ${response.status}`);
    }
    return response.blob();
}

async function _drainLivePreviewQueue() {
    if (_livePreviewState.inflight) return;
    const queue = Array.isArray(_livePreviewState.queue) ? _livePreviewState.queue : [];
    const next = queue.length ? queue.shift() : null;
    if (!next) return;
    _livePreviewState.queue = queue;

    if (next.jobId !== _livePreviewState.jobId) {
        _livePreviewState.jobId = next.jobId;
        _livePreviewState.latestVersion = 0;
    }
    if (next.version && next.version <= _livePreviewState.latestVersion) {
        if (_livePreviewState.queue.length) await _drainLivePreviewQueue();
        return;
    }

    _livePreviewState.inflight = true;
    const currentToken = _livePreviewState.token;
    const now = Date.now();
    const earliestStart = _livePreviewState.lastFetchStartedAt + LIVE_PREVIEW_MIN_FETCH_MS;
    if (earliestStart > now) {
        await new Promise((resolve) => setTimeout(resolve, earliestStart - now));
    }
    _livePreviewState.lastFetchStartedAt = Date.now();
    const controller = new AbortController();
    _livePreviewState.abortController = controller;

    try {
        const blob = await _fetchLivePreviewFrame(next.url, controller.signal);
        if (currentToken !== _livePreviewState.token) return;
        if (next.jobId !== _livePreviewState.jobId) return;

        const container = document.getElementById('compareContainer');
        const resImg = document.getElementById('resImg');
        const origImg = document.getElementById('origImg');
        if (!resImg || !origImg || !container) return;

        const objectURL = URL.createObjectURL(blob);
        if (resImg.dataset.blobUrl) {
            try {
                URL.revokeObjectURL(resImg.dataset.blobUrl);
            } catch (_e) {}
        }
        resImg.dataset.blobUrl = objectURL;
        resImg.dataset.displayUrl = next.url;

        const sourcePreviewEl = document.getElementById('sourcePreview');
        if (!origImg.getAttribute('src') && sourcePreviewEl && sourcePreviewEl.getAttribute('src')) {
            origImg.src = sourcePreviewEl.getAttribute('src');
        }

        await new Promise((resolve) => {
            let settled = false;
            const done = () => {
                if (settled) return;
                settled = true;
                resolve();
            };
            resImg.onload = () => {
                const resultCard = document.getElementById('resultCard');
                if (resultCard && resultCard.classList.contains('is-loading')) {
                    resultCard.classList.add('has-live-preview');
                    resultCard.style.setProperty('--build-frontier-pct', `${_clampPct(next.frontierPct)}%`);
                }
                container.style.minHeight = '0px';
                resImg.classList.remove('img-loading');
                origImg.classList.remove('img-loading');
                runDeferredRefit(container, resImg, origImg);
                updateCompareDebug();
                done();
            };
            resImg.onerror = () => done();
            resImg.src = objectURL;
            if (resImg.complete && resImg.naturalWidth) {
                window.requestAnimationFrame(() => {
                    const resultCard = document.getElementById('resultCard');
                    if (resultCard && resultCard.classList.contains('is-loading')) {
                        resultCard.classList.add('has-live-preview');
                        resultCard.style.setProperty('--build-frontier-pct', `${_clampPct(next.frontierPct)}%`);
                    }
                    container.style.minHeight = '0px';
                    resImg.classList.remove('img-loading');
                    origImg.classList.remove('img-loading');
                    runDeferredRefit(container, resImg, origImg);
                    updateCompareDebug();
                    done();
                });
            }
        });

        _livePreviewState.latestVersion = Math.max(_livePreviewState.latestVersion, Number(next.version || 0));
    } catch (_e) {
        // Best-effort UX enhancement: final result delivery remains authoritative.
    } finally {
        if (_livePreviewState.abortController === controller) {
            _livePreviewState.abortController = null;
        }
        _livePreviewState.inflight = false;
        if (_livePreviewState.queue.length) {
            await _drainLivePreviewQueue();
        }
    }
}

export function clearLivePreviewState() {
    _clearLivePreviewQueueState();
}

export function applyLivePreview(detail = {}) {
    const payload = _extractLivePreviewPayload(detail);
    if (!payload || !payload.url) return;

    if (_livePreviewState.jobId && _livePreviewState.jobId !== payload.jobId) {
        _clearLivePreviewQueueState();
    }
    _livePreviewState.jobId = payload.jobId;
    if (payload.version && payload.version <= _livePreviewState.latestVersion) {
        return;
    }
    const queue = Array.isArray(_livePreviewState.queue) ? _livePreviewState.queue : [];
    const lastQueued = queue.length ? queue[queue.length - 1] : null;
    if (
        lastQueued
        && Number(payload.version || 0) > 0
        && Number(lastQueued.version || 0) > 0
        && Number(payload.version || 0) <= Number(lastQueued.version || 0)
    ) {
        return;
    }
    queue.push(payload);
    // Keep a short queue so frame updates stay smooth without going stale.
    if (queue.length > 4) {
        queue.splice(0, queue.length - 4);
    }
    _livePreviewState.queue = queue;
    _drainLivePreviewQueue().catch(() => {});
}

export function updateBuildFrontier(detail = {}) {
    const raw = (detail && detail.raw) ? detail.raw : {};
    const phase = String(detail.phase || raw.phase || '').trim().toLowerCase();
    if (!LIVE_PREVIEW_PHASES.has(phase)) return;
    const pct = _clampPct(
        detail.pct
        ?? raw.pct
        ?? ((Number(raw.total || 0) > 0) ? ((Number(raw.current || 0) / Number(raw.total || 1)) * 100) : 0)
    );
    const resultCard = document.getElementById('resultCard');
    if (!resultCard || !resultCard.classList.contains('is-loading')) return;
    if (!resultCard.classList.contains('build-preview-mode')) {
        resultCard.classList.add('build-preview-mode');
    }
    resultCard.style.setProperty('--build-frontier-pct', `${pct}%`);
}

export function showEmpty(message = '') {
    clearLivePreviewState();
    resetSocialVideoUi();
    const empty = getViewerEmpty();
    const content = getViewerContent();
    if (empty) {
        if (!message) {
            refreshViewerEmptyState();
        } else {
            setViewerEmptyText(message);
        }
        empty.classList.remove('hidden');
    }
    if (content) content.classList.add('hidden');
    const debug = document.getElementById('compareDebug');
    if (debug) debug.classList.add('hidden');
    state.viewerJobId = null;
}

export function showContent() {
    const empty = getViewerEmpty();
    const content = getViewerContent();
    if (empty) empty.classList.add('hidden');
    if (content) content.classList.remove('hidden');
}

export function applyPreviewLayout(origW, origH) {
    const container = document.getElementById('compareContainer');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    if (!container || !resImg || !origImg) return;
    fitCompareToSize(container, resImg, origImg, origW, origH);
    updateCompareDebug();
}

export function prepareLoadingState(origW = 0, origH = 0, options = {}) {
    const shouldExpandViewer = Boolean(options && options.expandViewer);
    clearLivePreviewState();
    showContent();
    if (shouldExpandViewer) {
        expandViewer();
    }
    const resultCard = document.getElementById('resultCard');
    const loader = document.getElementById('resultLoader');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    const overlay = document.getElementById('overlayDiv');
    const container = document.getElementById('compareContainer');

    if (resultCard) {
        resultCard.classList.remove('compare-intro-mode');
        resultCard.classList.remove('has-live-preview');
        resultCard.classList.add('is-loading');
        resultCard.classList.add('build-preview-mode');
        resultCard.style.setProperty('--build-frontier-pct', '0%');
    }
    if (loader) {
        loader.innerHTML = RESULT_LOADER_DEFAULT_HTML;
        loader.classList.remove('hidden');
    }
    if (resImg) resImg.classList.add('img-loading');
    if (origImg) origImg.classList.add('img-loading');
    if (overlay) overlay.style.width = '50%';
    const sourcePreviewEl = document.getElementById('sourcePreview');
    if (origImg && sourcePreviewEl) {
        const sourceSrc = String(sourcePreviewEl.getAttribute('src') || '').trim();
        if (sourceSrc && origImg.getAttribute('src') !== sourceSrc) {
            origImg.src = sourceSrc;
        }
        if (sourceSrc) {
            _primeHiddenSourcePreload(sourceSrc).catch(() => {});
        }
    }
    if (container && Number(origW || 0) > 0 && Number(origH || 0) > 0) {
        container.style.minHeight = '0px';
        _runDeferredBuildLayout(container, resImg, origImg, Number(origW), Number(origH));
    } else if (container) {
        container.style.width = '100%';
        container.style.height = 'auto';
        container.style.minHeight = 'min(320px, 56vh)';
        container.style.maxWidth = '100%';
        container.style.marginLeft = 'auto';
        container.style.marginRight = 'auto';
    }
    // Do not pre-fit on job start; layout is still transitioning and may be collapsed.
    // Final fit is applied when result image loads and on workflow transition end.
    bindWorkflowRefit();
    updateCompareDebug();
}

export function refitViewer() {
    const container = document.getElementById('compareContainer');
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    if (!container || !resImg || !origImg) return;
    if (!resImg.naturalWidth || !resImg.naturalHeight) return;
    runDeferredRefit(container, resImg, origImg);
}

function updateNerdStatsPopover(hasConfig) {
    const pop = document.getElementById('nerdStatsPop');
    if (!pop) return;
    pop.classList.toggle('hidden', !hasConfig);
    if (!hasConfig) {
        pop.classList.remove('is-open');
        const btn = document.getElementById('nerdStatsBtn');
        if (btn) btn.setAttribute('aria-expanded', 'false');
    }
}

function bindNerdStatsPopover() {
    if (nerdStatsBound) return;
    const pop = document.getElementById('nerdStatsPop');
    const btn = document.getElementById('nerdStatsBtn');
    if (!pop || !btn) return;
    nerdStatsBound = true;
    btn.setAttribute('aria-expanded', 'false');
}

// Moves the complex "showResult" logic out of ui.js
export async function show(
    url,
    sourceUrl = null,
    config = null,
    downloadUrl = null,
    inspectUrl = null,
    viewerJobId = '',
    options = {},
) {
    _clearLivePreviewQueueState({ keepBuildClasses: true });
    showContent();
    expandViewer();
    bindWorkflowRefit();
    if (viewerJobId) {
        state.viewerJobId = String(viewerJobId);
    } else if (!state.viewerJobId) {
        state.viewerJobId = String(state.selectedHistoryJobId || state.jobId || '');
    }
    refreshActiveSocialVideoUi();
    const resImg = document.getElementById('resImg');
    const origImg = document.getElementById('origImg');
    const container = document.getElementById('compareContainer');
    const overlay = document.getElementById('overlayDiv');
    const loader = document.getElementById('resultLoader');
    const resultCard = document.getElementById('resultCard');
    const transitioningFromBuildPreview = Boolean(
        resultCard
        && resultCard.classList.contains('is-loading')
        && resultCard.classList.contains('build-preview-mode')
        && resultCard.classList.contains('has-live-preview')
    );
    const keepPreviewVisible = Boolean(
        transitioningFromBuildPreview
        && resImg
        && String(resImg.getAttribute('src') || '').trim()
    );
    if (resultCard) {
        resultCard.classList.remove('compare-intro-mode');
        if (!keepPreviewVisible) {
            resultCard.classList.remove('has-live-preview');
            resultCard.classList.remove('build-preview-mode');
            resultCard.style.removeProperty('--build-frontier-pct');
        }
    }
    let resolveLoad;
    const loadPromise = new Promise((resolve) => { resolveLoad = resolve; });
    let loadResolved = false;
    let compareIntroStarted = false;
    let finalMosaicReady = false;
    let finalSourceReady = false;
    let compareIntroDone = false;
    const introHoldRaw = Number(options && options.introHoldMs);
    const introHoldMs = Number.isFinite(introHoldRaw) ? Math.max(0, introHoldRaw) : 1000;
    const skipImageFetch = Boolean(options && options.skipImageFetch);
    const skipCompareIntro = Boolean(options && options.skipCompareIntro);
    const skipSourceWait = Boolean(options && options.skipSourceWait);
    const popOutOnReady = Boolean(options && options.popOutOnReady);
    const isSourceGateSatisfied = () => (finalSourceReady || skipSourceWait);
    const maybeResolveLoad = () => {
        if (loadResolved) return;
        if (!finalMosaicReady) return;
        if (!isSourceGateSatisfied()) return;
        if (!compareIntroDone) return;
        loadResolved = true;
        if (resolveLoad) resolveLoad();
    };
    const maybeStartCompareIntro = () => {
        if (compareIntroStarted) return;
        if (!finalMosaicReady || !isSourceGateSatisfied()) return;
        compareIntroStarted = true;
        const introToken = ++_compareIntroToken;
        const startIntro = async () => {
            if (skipCompareIntro) {
                if (resultCard) {
                    if (loader) loader.classList.add('hidden');
                    resultCard.classList.remove('compare-intro-mode');
                    resultCard.classList.remove('build-preview-mode');
                    resultCard.classList.remove('has-live-preview');
                    resultCard.style.removeProperty('--build-frontier-pct');
                }
                if (overlay) {
                    overlay.style.width = '50%';
                    overlay.style.visibility = (skipSourceWait && !finalSourceReady) ? 'hidden' : '';
                }
                compareIntroDone = true;
                maybeResolveLoad();
                return;
            }
            // Hold final built mosaic for a brief beat before showing compare.
            if (introHoldMs > 0) {
                await new Promise((resolve) => window.setTimeout(resolve, introHoldMs));
            }
            if (introToken !== _compareIntroToken) {
                compareIntroDone = true;
                maybeResolveLoad();
                return;
            }
            if (resultCard) {
                if (loader) loader.classList.add('hidden');
                resultCard.classList.add('compare-intro-mode');
                resultCard.classList.remove('build-preview-mode');
                resultCard.classList.remove('has-live-preview');
                resultCard.style.removeProperty('--build-frontier-pct');
            }
            if (overlay) {
                overlay.style.visibility = '';
                await _playCompareIntroSweep(overlay).catch(() => {});
            }
            compareIntroDone = true;
            maybeResolveLoad();
        };
        if (overlay) {
            overlay.style.visibility = 'hidden';
        }
        startIntro().catch(() => {
            compareIntroDone = true;
            maybeResolveLoad();
        });
    };

    if (loader && !keepPreviewVisible) {
        loader.innerHTML = RESULT_LOADER_DEFAULT_HTML;
        loader.classList.remove('hidden');
    }
    if (!keepPreviewVisible) {
        resImg.classList.add('img-loading');
    }
    origImg.classList.add('img-loading');
    if (overlay) {
        if (!(skipCompareIntro && skipImageFetch && !skipSourceWait)) {
            overlay.style.visibility = 'hidden';
        }
    }
    const revealBuiltPreviewFully = () => {
        if (!resultCard || !resultCard.classList.contains('is-loading')) return;
        if (!resultCard.classList.contains('build-preview-mode')) return;
        if (!resultCard.classList.contains('has-live-preview')) return;
        resultCard.style.setProperty('--build-frontier-pct', '100%');
    };

    // Store inspect URL for Zoom Modal (fallback to display URL if inspect missing)
    resImg.dataset.inspectUrl = inspectUrl || url;
    resImg.dataset.displayUrl = url || '';

    const headers = {};

    const _applyDownloadTargets = () => {
        const dlLink = document.getElementById('downloadLink');
        const finalDownloadHref = addDownloadFlag(downloadUrl || url);
        if (dlLink) {
            dlLink.href = finalDownloadHref;
            dlLink.download = _downloadFilename(DRAFT_DIRECT_DOWNLOAD_EDGE);
        }
        (async () => {
            await _refreshBillingStatus();
            await _refreshFinalizeOptions(_activeJobId());
            _renderDownloadButtons(downloadUrl || url, config || {});
        })().catch(() => {
            _renderDownloadButtons(downloadUrl || url, config || {});
        });
    };

    const canReuseCurrentImage = Boolean(
        skipImageFetch
        && String(resImg.getAttribute('src') || '').trim()
    );
    if (canReuseCurrentImage) {
        resImg.classList.remove('img-loading');
        finalMosaicReady = true;
        maybeStartCompareIntro();
        maybeResolveLoad();
        _applyDownloadTargets();
    } else {
        try {
            const resp = await fetch(url, { headers });
            if (resp.status === 403 || resp.status === 401) {
                throw new Error("Access Denied - Private Image");
            }
            if (!resp.ok) throw new Error("Load failed");

            const blob = await resp.blob();
            const objectURL = URL.createObjectURL(blob);

            if (resImg.dataset.blobUrl) {
                URL.revokeObjectURL(resImg.dataset.blobUrl);
            }
            resImg.dataset.blobUrl = objectURL;
            resImg.onload = () => {
                runDeferredRefit(container, resImg, origImg);
                revealBuiltPreviewFully();
                resImg.classList.remove('img-loading');
                finalMosaicReady = true;
                maybeStartCompareIntro();
                maybeResolveLoad();
            };
            resImg.onerror = () => {
                if (loader) loader.innerHTML = `<span style="color:#e53e3e">⚠️ Failed to load mosaic image.</span>`;
                resImg.classList.remove('img-loading');
                finalMosaicReady = true;
                finalSourceReady = true;
                compareIntroDone = true;
                maybeStartCompareIntro();
                origImg.classList.remove('img-loading');
                updateCompareDebug();
                maybeResolveLoad();
            };

            resImg.src = objectURL;
            if (resImg.complete && resImg.naturalWidth) {
                window.requestAnimationFrame(() => {
                    revealBuiltPreviewFully();
                    runDeferredRefit(container, resImg, origImg);
                });
            }
            _applyDownloadTargets();
        } catch (e) {
            console.error("Secure Image Load Failed:", e);
            if (loader) loader.innerHTML = `<span style="color:#e53e3e">⚠️ ${e.message}</span>`;
            // Ensure image isn't stuck invisible
            resImg.classList.remove('img-loading');
            finalMosaicReady = true;
            finalSourceReady = true;
            compareIntroDone = true;
            maybeStartCompareIntro();
            origImg.classList.remove('img-loading');
            maybeResolveLoad();
        }
    }

    // Set source image (reuse preloaded source when possible to avoid "source still loading" after completion)
    const sourcePreviewEl = document.getElementById('sourcePreview');
    const existingSource = String(origImg.getAttribute('src') || '').trim();
    const existingReady = Boolean(origImg.complete && origImg.naturalWidth > 0);
    let finalSource = String(sourceUrl || (sourcePreviewEl ? sourcePreviewEl.src : '') || '').trim();
    if (!finalSource && sourcePreviewEl && sourcePreviewEl.src) {
        finalSource = String(sourcePreviewEl.src || '').trim();
    }
    if (finalSource) {
        _primeHiddenSourcePreload(finalSource).catch(() => {});
    }
    origImg.onerror = () => {
        if (!sourcePreviewEl || !sourcePreviewEl.src) {
            finalSourceReady = true;
            origImg.classList.remove('img-loading');
            if (overlay && skipCompareIntro && skipSourceWait) {
                overlay.style.visibility = '';
                overlay.style.width = '50%';
            }
            maybeStartCompareIntro();
            maybeResolveLoad();
            return;
        }
        if (origImg.src !== sourcePreviewEl.src) {
            origImg.src = sourcePreviewEl.src;
        }
        finalSourceReady = true;
        origImg.classList.remove('img-loading');
        if (overlay && skipCompareIntro && skipSourceWait) {
            overlay.style.visibility = '';
            overlay.style.width = '50%';
        }
        maybeStartCompareIntro();
        maybeResolveLoad();
    };
    origImg.onload = () => {
        finalSourceReady = true;
        origImg.classList.remove('img-loading');
        if (overlay && skipCompareIntro && skipSourceWait) {
            overlay.style.visibility = '';
            overlay.style.width = '50%';
        }
        maybeStartCompareIntro();
        maybeResolveLoad();
        runDeferredRefit(container, resImg, origImg);
    };
    if (finalSource) {
        const existingIdentity = _sourceIdentity(existingSource);
        const finalIdentity = _sourceIdentity(finalSource);
        const sameReadySource = Boolean(
            existingReady
            && existingIdentity
            && finalIdentity
            && existingIdentity === finalIdentity
        );
        if (sameReadySource) {
            finalSourceReady = true;
            origImg.classList.remove('img-loading');
            maybeStartCompareIntro();
            maybeResolveLoad();
        } else {
            const currentSource = String(origImg.getAttribute('src') || '').trim();
            if (currentSource !== finalSource || !origImg.complete || origImg.naturalWidth <= 0) {
                origImg.src = finalSource;
            }
        }
    } else if (!finalSourceReady) {
        finalSourceReady = true;
        origImg.classList.remove('img-loading');
        maybeStartCompareIntro();
        maybeResolveLoad();
    }
    origImg.dataset.sourceUrl = finalSource || '';
    overlay.style.width = "50%";
    updateCompareDebug();

    if (!resizeHandlerAttached) {
        resizeHandlerAttached = true;
        window.addEventListener('resize', () => {
            fitCompareToViewport(container, resImg, origImg);
            updateCompareDebug();
        });
    }

    const cfgDiv = document.getElementById('jobConfig');
    const hasConfig = Boolean(config && Object.keys(config).length > 0);
    bindNerdStatsPopover();
    updateNerdStatsPopover(hasConfig);
    if (cfgDiv) {
        cfgDiv.classList.toggle('hidden', !hasConfig);
        const safeSet = (id, value) => {
            const el = document.getElementById(id);
            if (el) el.innerText = value;
        };
        if (hasConfig) {
            const cMode = config.color_mode || "-";
            const matcherBackendRaw = String(config.matcher_backend || 'auto').trim().toLowerCase();
            const matcherBackend = matcherBackendRaw === 'faiss'
                ? 'FAISS'
                : (matcherBackendRaw === 'exact' ? 'Legacy exact' : 'Auto');
            safeSet('cfgColorStyle', cMode !== "-" ? cMode.charAt(0).toUpperCase() + cMode.slice(1) : "-");
            const units = normalizeSettingUnits(config.settings_units || '');
            const tileHeight = (config.base_tile_height != null) ? toUiTileHeight(config.base_tile_height) : null;
            const colorShare = (config.color_share != null) ? toUiColorShare(config.color_share, units) : null;
            const noiseFactor = (config.noise_factor != null) ? toUiNoiseFactor(config.noise_factor, units) : null;
            const reusePenalty = (config.reuse_penalty != null) ? toUiReusePenalty(config.reuse_penalty, units) : null;
            safeSet('cfgHeight', Number.isFinite(Number(tileHeight)) ? `${Math.round(Number(tileHeight))}px` : '-');
            safeSet('cfgColorShare', Number.isFinite(Number(colorShare)) ? String(Math.round(Number(colorShare))) : '-');
            safeSet('cfgNoise', Number.isFinite(Number(noiseFactor)) ? String(Math.round(Number(noiseFactor))) : '-');
            safeSet('cfgReuse', Number.isFinite(Number(reusePenalty)) ? String(Math.round(Number(reusePenalty))) : '-');
            safeSet('cfgMatcher', matcherBackend);
        } else {
            ['cfgColorStyle', 'cfgHeight', 'cfgColorShare', 'cfgNoise', 'cfgReuse', 'cfgMatcher'].forEach(id => {
                safeSet(id, "-");
            });
        }
    }

    const moveCompare = (e) => {
        if(e.cancelable) e.preventDefault();
        const rect = container.getBoundingClientRect();
        let clientX = e.clientX;
        if (e.touches && e.touches[0]) clientX = e.touches[0].clientX;
        let pct = ((clientX - rect.left) / rect.width) * 100;
        if(pct < 0) pct = 0;
        if(pct > 100) pct = 100;
        overlay.style.width = pct + "%";
    };

    container.onmousemove = moveCompare;
    container.ontouchmove = moveCompare;
    container.onclick = moveCompare;
    if (popOutOnReady) {
        loadPromise
            .then(() => _runViewerPopoutAnimation())
            .catch(() => {})
            .finally(() => {
                document.body.classList.remove('viewer-popout-running');
            });
    }
    return loadPromise;
}
